import {older_generation_cost_adjustment,get_skip_shape_regex} from "./dbcsi_external.js";


/*

Summary:
The function `pick_rds_shape` selects the most suitable RDS (Relational Database Service) shape based on given configuration parameters and region information. It compares various RDS shapes against the required specifications, considering factors like VCPU, memory, throughput, and IOPS. The function also handles different RDS types (e.g., standard, SE, custom, RAC) and optimizes for cost or performance based on configuration settings.

Description:
1. The function initializes working variables with configuration values, adjusting them if necessary.
2. It determines the sorting method for RDS shapes based on CPU optimization settings.
3. The function then iterates through available RDS shapes, filtering out incompatible options based on RDS type and other criteria.
4. For each compatible shape, it checks if it meets or exceeds the required specifications.
5. If an exact match is found, it's selected. Otherwise, the function tracks the closest match.
6. The function handles special cases like RAC configurations and bursting instances.
7. Finally, it returns the selected RDS shape along with additional information about the match or closest alternative.

Parameters:
1. regionInfo (object):
   - shapes (array): Contains information about available RDS shapes in the region. An array of different RDS instance shapes available in the region. Each shape is represented as an object with the following properties:
     - `Name`: The name of the RDS instance shape (e.g., "db.r5.large", "db.m6i.large").
     - `VCPU`: The number of virtual CPUs (VCPU) for the instance shape.
     - `Memory`: The amount of memory (in GB) for the instance shape.
     - `Throughput`: The maximum throughput (in MB/s) for the instance shape.
     - `BaselineThroughput`: The baseline throughput (in MB/s) for the instance shape (used when bursting is disabled).
     - `IOPS`: The maximum IOPS for the instance shape.
     - `BaselineIOPS`: The baseline IOPS for the instance shape (used when bursting is disabled).
     - `OracleOD`: The on-demand price for the Oracle database running on the instance shape.
     - `OracleLIse2_OD`: The on-demand price for the Oracle database running on the instance shape in the SE2 (Standard Edition 2) configuration.
     - `OracleCustomOD`: The on-demand price for the Oracle database running on the instance shape in the Custom configuration.
     - `Oracle1yrNURI`: The 1-year no-upfront reserved instance price for the Oracle database running on the instance shape.
     - `OracleLIse2_1yrNURI`: The 1-year no-upfront reserved instance price for the Oracle database running on the instance shape in the SE2 configuration.
     - `OracleCustom1yrNURI`: The 1-year no-upfront reserved instance price for the Oracle database running on the instance shape in the Custom configuration.
     - `OptimizeVCPUs`: Indicates whether the instance shape is eligible for CPU optimization.
     - `InstanceStorageGB`: The amount of instance storage (in GB) for the instance shape (used when the `nvme_shape_algo` is set to "Y").

2. config (object):
   - IOPS (number): Required Input/Output Operations Per Second.
   - VCPU (number): Required Virtual CPU count.
   - Memory (number): Required memory in GB.
   - Throughput (number): Required throughput in MB/s.
   - DiskSpace (number): Required disk space.
   - AdjustedIOPS (number, optional): Adjusted IOPS value.
   - AdjustedMemory (number, optional): Adjusted memory value.
   - AdjustedThroughput (number, optional): Adjusted throughput value.
   - AdjustedVCPU (number, optional): Adjusted VCPU value.
   - optgoal_choice (string): CPU optimization setting ('Y', 'min_cost', or 'min_license').
   - rds_type (string): Type of RDS instance ('se', 'custom', 'rac', or standard).
   - UseRI (string): Reserved Instance usage setting.
   - divider (number): Number of RAC nodes (for RAC configurations).
   - frozen_prefs (object): Contains additional preference settings.
   
This function returns an rdsItemSelected object. Based on the code, we can infer the following structure and properties of the rdsItemSelected object:

1. Name: String
   - Represents the name of the RDS instance type (e.g., "db.m5.large")
2. VCPU: Number
   - The number of vCPUs for the instance type
3. Memory: Number
   - The amount of memory (in GB) for the instance type
4. Throughput: Number
   - The maximum throughput capability (in MB/s) of the instance type
5. IOPS: Number
   - The maximum IOPS (Input/Output Operations Per Second) capability of the instance type
6. BaselineThroughput: Number
   - The baseline throughput (in MB/s) of the instance type (if applicable)
7. BaselineIOPS: Number
   - The baseline IOPS of the instance type (if applicable)
8. OracleOD: Number
   - The On-Demand pricing for Oracle database on this instance type
9. Oracle1yrNURI: Number
   - The 1-year No Upfront Reserved Instance pricing for Oracle database on this instance type
10. OracleLIse2_OD: Number
    - The On-Demand pricing for Oracle Standard Edition 2 (License Included) on this instance type
11. OracleLIse2_1yrNURI: Number
    - The 1-year No Upfront Reserved Instance pricing for Oracle Standard Edition 2 (License Included) on this instance type
12. OracleCustomOD: Number
    - The On-Demand pricing for Oracle Custom on this instance type (if applicable)
13. OracleCustom1yrNURI: Number
    - The 1-year No Upfront Reserved Instance pricing for Oracle Custom on this instance type (if applicable)
14. OptimizeVCPUs: String
    - A comma-separated list of available vCPU options for Optimize CPU feature (if applicable)
15. InstanceStorage: String or Boolean
    - Indicates if the instance type has instance storage and possibly its characteristics
16. InstanceStorageGB: Number
    - The size of instance storage in GB (if applicable)
17. closeness_string: String
    - A string indicating how close this instance type matches the requested configuration
18. match_string: String
    - A string providing details on how this instance type matches or differs from the requested configuration
19. gap_memory: Number
    - The difference between requested memory and this instance type's memory
20. gap_throughput: Number
    - The difference between requested throughput and this instance type's throughput capability
21. gap_iops: Number
    - The difference between requested IOPS and this instance type's IOPS capability
22. gap_vcpu: Number
    - The difference between requested vCPUs and this instance type's vCPU count

This object structure provides a comprehensive set of attributes that describe the capabilities, pricing, and suitability of a specific RDS instance type for the given requirements. The function uses these attributes to calculate costs, determine if the instance meets the performance requirements, and provide detailed information about the selected configuration.

   
*/

// ***********************************
// Summary: Return how many VCPUs AWS exposes per physical core for a shape.
// Description: Most shapes enable simultaneous multithreading (SMT), so 2 VCPUs = 1 physical
//   core.  Some shapes ship with SMT disabled and expose 1 VCPU per physical core - the AMD
//   EPYC 9R14 (c7a/m7a/r7a) and 9R05 (x8aedz) shapes, every Graviton shape, and the RDS
//   ".tpc1." shapes.  Those carry "ThreadsPerCore": 1 in shapes.json; everything else is
//   assumed to be 2.  Oracle licenses by physical core, so this drives the core counts.
// Parameters:
//   shape - a shape document from shapes.json (regionInfo.shapes)
// Returns: VCPUs per physical core (1 or 2)
// ***********************************
function   vcpus_per_core(shape) {
  return (shape && shape.ThreadsPerCore * 1 > 0) ? shape.ThreadsPerCore * 1 : 2;
}


function   pick_rds_shape(regionInfo, config) {

// **************************
// RDS Configuration
// **************************

// regionInfo.shapes = array of different RDS Shapes to consider
// config = details about the target values and other properties of the database

// UseRI values
// RI
// SP
// OD
// RI+SP (pick RI, SP, or OD).  Once you find a shape, set to appropriate value
// SP-Only (only pick SP shapes).  Once you find a shape, set to SP

     var working_IOPS = config.IOPS;
     var working_VCPU = config.VCPU;
     var working_Memory = config.Memory;
     var working_Throughput = config.Throughput;
     var working_DiskSpace = config.DiskSpace;

      // set working_IOPS equal to config.AdjustedIOPS if it exists else set it to config.IOPS
      if (config.AdjustedIOPS) {
        working_IOPS = config.AdjustedIOPS;
      }
      if (config.AdjustedMemory) {
        working_Memory = config.AdjustedMemory;
        // console.log("pick_rds_shape", "Using AdjustedMemory", working_Memory);
        }
      if(config.AdjustedThroughput) {
         working_Throughput = config.AdjustedThroughput;
       }
      if (config.AdjustedVCPU) {
        working_VCPU = config.AdjustedVCPU;
      }



      //Now work on RDS
      // console.log("pick_rds_shape", config.rds_type);
      
      //
     // optimizeCPU=Y and SE2 or Custom doesn't make sense 
      if ( (config.optgoal_choice=='Y') && (config.rds_type=='se' || config.rds_type=='custom') ) {
        config.optgoal_choice='min_license';
      }

        var rdsItems = regionInfo.shapes;

      //Step1 - match to rdsItems
      
      var rdsItemSelected = null;
      let bareMetalMatch = null;
      let closeness = -999;
      let closeness_string = "";
      let match_string = "";

      let gap_memory=  0;
      let gap_throughput=  0;
      let gap_iops=  0;
      let gap_vcpu=  0;
      let max_storage_throughput = 16000;
      let divider=1; //number of rac nodes
      
      
      if (config.rds_type=="rac") {
        divider = config.divider;
        max_storage_throughput = 4000*16;
      }  
      
        function rdsCompareCost(a, b) {
          // what price fields to use
          var ODfield = "OracleOD";
          var RIfield = "Oracle1yrNURI";
          var SPfield = "Oracle1yrNUSP";
          if ((config.rds_type=="se")) {
            ODfield = "OracleLIse2_OD";
            RIfield = "OracleLIse2_1yrNURI";
            SPfield = null;
          }
          if ((config.rds_type=="custom")) {
            ODfield = "OracleCustomOD";
            RIfield = "OracleCustom1yrNURI";
            SPfield = null;
          }
          
          // RI only use RI
          // SP only use SP
          // OD only use OD
          // RI+SP (pick RI, SP, or OD).  Use lowest cost
          // SP-Only (only pick SP shapes).  Only use SP

          
          //config.UseRI could be config.frozen_prefs.useRI_algo.  I think we use UseRI for manual edit override.
          // const itemA = ((config.UseRI=='RI') && (config.rds_type!="rac") && (a[RIfield] * 1 > 0) ? a[RIfield]*1 : a[ODfield]*1) * 10000 - a.VCPU*1 ;
          // const itemB = ((config.UseRI=='RI') && (config.rds_type!="rac") && (b[RIfield] * 1 > 0) ? b[RIfield]*1 : b[ODfield]*1) * 10000 - b.VCPU*1;
          var itemA = 0;
          var itemB = 0;
          if (config.UseRI=="RI+SP") {
            itemA =  older_generation_cost_adjustment(Math.min(a[ODfield]*1, 
                          ( RIfield!=null && (a[RIfield] * 1 > 0) ? a[RIfield]*1 : 9999),
                          ( SPfield!=null && (a[SPfield] * 1 > 0) ? a[SPfield]*1 : 9999)
                          ), a) * 10000 - a.VCPU*1;
            itemB =  older_generation_cost_adjustment(Math.min(b[ODfield]*1, 
                          ( RIfield!=null && (b[RIfield] * 1 > 0) ? b[RIfield]*1 : 9999),
                          ( SPfield!=null && (b[SPfield] * 1 > 0) ? b[SPfield]*1 : 9999)
                          ), b) * 10000 - b.VCPU*1;              
          }
          if ((config.UseRI=="SP-Only") || (config.UseRI=="SP") ) {
            itemA =  older_generation_cost_adjustment(Math.min(a[ODfield]*1, 
                          ( SPfield!=null && (a[SPfield] * 1 > 0) ? a[SPfield]*1 : 9999)
                          ), a) * 10000 - a.VCPU*1;
            itemB =  older_generation_cost_adjustment(Math.min(b[ODfield]*1, 
                          ( SPfield!=null && (b[SPfield] * 1 > 0) ? b[SPfield]*1 : 9999)
                          ), b) * 10000 - b.VCPU*1;              
          }
          if (config.UseRI=="RI") {
            itemA =  older_generation_cost_adjustment(Math.min(a[ODfield]*1, 
                          ( RIfield!=null && (a[RIfield] * 1 > 0) ? a[RIfield]*1 : 9999)
                          ), a) * 10000 - a.VCPU*1;
            itemB =  older_generation_cost_adjustment(Math.min(b[ODfield]*1, 
                          ( RIfield!=null && (b[RIfield] * 1 > 0) ? b[RIfield]*1 : 9999)
                          ), b) * 10000 - b.VCPU*1;              
           }
          if (config.UseRI=="OD") {
            itemA =  older_generation_cost_adjustment(Math.min(a[ODfield]*1
                          ), a) * 10000 - a.VCPU*1;
            itemB =  older_generation_cost_adjustment(Math.min(b[ODfield]*1
                          ), b) * 10000 - b.VCPU*1;              
           }
          
          
          // we adding the VCPU  in the calculation even though we are comparing cost.  I am not sure why...  
          // the end result is that we will pick a r5.xlarge.tpc2.mem2x over a r6i.2xlarge or r5.2xlarge.  
          // That seems counter-intuitive-- if we are picking by cost, why not get the better shape.
          // In July 2025, I am changing the expression to subtract the VCPU.  That means if the price is the same, pick the one with more VCPUs

          let comparison = 0;
          if (itemA > itemB) {
            comparison = 1;
          } else if (itemA < itemB) {
            comparison = -1;
          } else if (itemA == itemB) {
            // final sort is by Name in REVERSE order.  This should make r6i.xlarge appear ahead of r5.xlarge
            if (a.Name.split(".")[0] > b.Name.split(".")[0]) {
              comparison = -1;
            } else if (a.Name.split(".")[0] < b.Name.split(".")[0]) {
              comparison = 1;
            }
          }
          return comparison;
        }

        function rdsCompareVCPU(a, b) {
          var ODfield = "OracleOD";
          var RIfield = "Oracle1yrNURI";
          var SPfield = "Oracle1yrNUSP";
          if ((config.rds_type=="se")) {
            ODfield = "OracleLIse2_OD";
            RIfield = "OracleLIse2_1yrNURI";
            SPfield = null;
          }
          if ((config.rds_type=="custom")) {
            ODfield = "OracleCustomOD";
            RIfield = "OracleCustom1yrNURI";
            SPfield = null;
          }
          
         // const itemA = a.VCPU * 10000 + ((config.UseRI=='RI') && (config.rds_type!="rac") && (a[RIfield] * 1 > 0) ? a[RIfield]*1 : a[ODfield]*1);
        //  const itemB = b.VCPU * 10000 + ((config.UseRI=='RI') && (config.rds_type!="rac") && (b[RIfield] * 1 > 0) ? b[RIfield]*1 : b[ODfield]*1);

          var itemA = 0;
          var itemB = 0;
          if (config.UseRI=="RI+SP") {
            itemA =  older_generation_cost_adjustment(Math.min(a[ODfield]*1, 
                          ( RIfield!=null && (a[RIfield] * 1 > 0) ? a[RIfield]*1 : 9999),
                          ( SPfield!=null && (a[SPfield] * 1 > 0) ? a[SPfield]*1 : 9999)
                          ), a) + 10000 * a.VCPU*1;
            itemB =  older_generation_cost_adjustment(Math.min(b[ODfield]*1, 
                          ( RIfield!=null && (b[RIfield] * 1 > 0) ? b[RIfield]*1 : 9999),
                          ( SPfield!=null && (b[SPfield] * 1 > 0) ? b[SPfield]*1 : 9999)
                          ), b) + 10000 * b.VCPU*1;              
          }
          if ((config.UseRI=="SP-Only") || (config.UseRI=="SP") ) {
            itemA =  older_generation_cost_adjustment(Math.min(a[ODfield]*1, 
                          ( SPfield!=null && (a[SPfield] * 1 > 0) ? a[SPfield]*1 : 9999)
                          ), a) + 10000 * a.VCPU*1;
            itemB =  older_generation_cost_adjustment(Math.min(b[ODfield]*1, 
                          ( SPfield!=null && (b[SPfield] * 1 > 0) ? b[SPfield]*1 : 9999)
                          ), b) + 10000 * b.VCPU*1;              
          }
          if (config.UseRI=="RI") {
            itemA =  older_generation_cost_adjustment(Math.min(a[ODfield]*1, 
                          ( RIfield!=null && (a[RIfield] * 1 > 0) ? a[RIfield]*1 : 9999)
                          ), a) + 10000 * a.VCPU*1;
            itemB =  older_generation_cost_adjustment(Math.min(b[ODfield]*1, 
                          ( RIfield!=null && (b[RIfield] * 1 > 0) ? b[RIfield]*1 : 9999)
                          ), b) + 10000 * b.VCPU*1;              
           }
          if (config.UseRI=="OD") {
            itemA =  older_generation_cost_adjustment(Math.min(a[ODfield]*1
                          ), a) + 10000 * a.VCPU*1;
            itemB =  older_generation_cost_adjustment(Math.min(b[ODfield]*1
                          ), b) + 10000 * b.VCPU*1;              
           }

          let comparison = 0;
          if (itemA > itemB) {
            comparison = 1;
          } else if (itemA < itemB) {
            comparison = -1;
          } else if (itemA == itemB) {
            // final sort is by Name in REVERSE order.  This should make r6i.xlarge appear ahead of r5.xlarge
            if (a.Name.split(".")[0] > b.Name.split(".")[0]) {
              comparison = -1;
            } else if (a.Name.split(".")[0] < b.Name.split(".")[0]) {
              comparison = 1;
            }
          }
          return comparison;
        }

        function rdsCompareVCPUandBareMetal(a, b) {
          // what price fields to use
          var ODfield = "OracleOD";
          var RIfield = "Oracle1yrNURI";
          var SPfield = "Oracle1yrNUSP";
          if ((config.rds_type=="se")) {
            ODfield = "OracleLIse2_OD";
            RIfield = "OracleLIse2_1yrNURI";
            SPfield = null;
          }
          if ((config.rds_type=="custom")) {
            ODfield = "OracleCustomOD";
            RIfield = "OracleCustom1yrNURI";
            SPfield = null;
          }
          
          // assume that BareMetal VCPUs count 0.5 what regular VCPUs count
          // const itemA = a.VCPU * (a.Name.includes("metal") ? 5000 : 10000) + ((config.UseRI=='RI') && (config.rds_type!="rac") && (a[RIfield] * 1 > 0) ? a[RIfield]*1 : a[ODfield]*1);
          // const itemB = b.VCPU * (b.Name.includes("metal") ? 5000 : 10000) + ((config.UseRI=='RI') && (config.rds_type!="rac") && (b[RIfield] * 1 > 0) ? b[RIfield]*1 : b[ODfield]*1);
          
          var itemA = 0;
          var itemB = 0;
          if (config.UseRI=="RI+SP") {
            itemA =  older_generation_cost_adjustment(Math.min(a[ODfield]*1, 
                          ( RIfield!=null && (a[RIfield] * 1 > 0) ? a[RIfield]*1 : 9999),
                          ( SPfield!=null && (a[SPfield] * 1 > 0) ? a[SPfield]*1 : 9999)
                          ), a) + (a.Name.includes("metal") ? 5000 : 10000) * a.VCPU*1;
            itemB =  older_generation_cost_adjustment(Math.min(b[ODfield]*1, 
                          ( RIfield!=null && (b[RIfield] * 1 > 0) ? b[RIfield]*1 : 9999),
                          ( SPfield!=null && (b[SPfield] * 1 > 0) ? b[SPfield]*1 : 9999)
                          ), b) + (b.Name.includes("metal") ? 5000 : 10000) * b.VCPU*1;              
          }
          if ((config.UseRI=="SP-Only") || (config.UseRI=="SP") ) {
            itemA =  older_generation_cost_adjustment(Math.min(a[ODfield]*1, 
                          ( SPfield!=null && (a[SPfield] * 1 > 0) ? a[SPfield]*1 : 9999)
                          ), a) + (a.Name.includes("metal") ? 5000 : 10000) * a.VCPU*1;
            itemB =  older_generation_cost_adjustment(Math.min(b[ODfield]*1, 
                          ( SPfield!=null && (b[SPfield] * 1 > 0) ? b[SPfield]*1 : 9999)
                          ), b) + (b.Name.includes("metal") ? 5000 : 10000) * b.VCPU*1;              
          }
          if (config.UseRI=="RI") {
            itemA =  older_generation_cost_adjustment(Math.min(a[ODfield]*1, 
                          ( RIfield!=null && (a[RIfield] * 1 > 0) ? a[RIfield]*1 : 9999)
                          ), a) + (a.Name.includes("metal") ? 5000 : 10000) * a.VCPU*1;
            itemB =  older_generation_cost_adjustment(Math.min(b[ODfield]*1, 
                          ( RIfield!=null && (b[RIfield] * 1 > 0) ? b[RIfield]*1 : 9999)
                          ), b) + (b.Name.includes("metal") ? 5000 : 10000) * b.VCPU*1;              
           }
          if (config.UseRI=="OD") {
            itemA =  older_generation_cost_adjustment(Math.min(a[ODfield]*1
                          ), a) + (a.Name.includes("metal") ? 5000 : 10000) * a.VCPU*1;
            itemB =  older_generation_cost_adjustment(Math.min(b[ODfield]*1
                          ), b) + (b.Name.includes("metal") ? 5000 : 10000) * b.VCPU*1;              
           }


          let comparison = 0;
          if (itemA > itemB) {
            comparison = 1;
          } else if (itemA < itemB) {
            comparison = -1;
          } else if (itemA == itemB) {
            // final sort is by Name in REVERSE order.  This should make r6i.xlarge appear ahead of r5.xlarge
            if (a.Name.split(".")[0] > b.Name.split(".")[0]) {
              comparison = -1;
            } else if (a.Name.split(".")[0] < b.Name.split(".")[0]) {
              comparison = 1;
            }
          }
          return comparison;
        }

        
      //if optgoal_choice is min_cost, sort by cost, then VCPU.  if optgoal_choice is min_license, sort by VCPU, then cost
      if (config.optgoal_choice=="min_license") {
        if ( ( (config.frozen_prefs.rds_baremetal_ranking=="consider0.5") || (config.frozen_prefs.rds_baremetal_ranking=="never") || (config.frozen_prefs.rds_baremetal_ranking=="considerVCPU")) && 
             ( (config.rds_type=="ee") || (config.rds_type=="custom") ) ){
          rdsItems.sort(rdsCompareVCPUandBareMetal);
          // console.log("sort by VCPUandBareMetal", rdsItems[0]);
        } else {
          rdsItems.sort(rdsCompareVCPU);
          // console.log("sort by VCPU", rdsItems[0]);
        }  
        
      } else  {
        rdsItems.sort(rdsCompareCost);
        // console.log("sort by Cost", rdsItems[0]);
      }  

     // Skip Shape Filter (prefs.rdsSkipShape_preset / rdsSkipShape_expr), shared by the RDS-Oracle, EC2, EC2+FSxN, and Aurora pickers
     var regex = get_skip_shape_regex(config.frozen_prefs);
     let doSkipRegex = (regex !== null);
      
      for (var bii = 0; bii < rdsItems.length; bii++) {
          
          //only process if Oracle record
          //AND skip any tpc1 shapes -- I don't see their value
          //AND skip any tpc* shapes if optgoal_choice=Y
        if ( (rdsItems[bii].OracleOD *1 >0) && !(rdsItems[bii].Name.includes('tpc1')) && !( config.optgoal_choice=='Y' && rdsItems[bii].Name.includes('tpc') )  ) {
          
          var okForRAC=false;
          const io2RACmax = 16*16000/2; //for io2, we can provision 16 volumes at 16TB each but we use ASM normal redundancy.  Anything bigger needs to use io2bx
          max_storage_throughput = 16000;
          if (config.rds_type=="rac") {
  
            //is this shape OK for RAC?
            if ( (rdsItems[bii].Name.substring(0,4)=="m6i.") && (working_DiskSpace*1 < io2RACmax) ) { okForRAC=true; max_storage_throughput = 1000*16; }
            if ( (rdsItems[bii].Name.substring(0,4)=="r6i.") && (working_DiskSpace*1 < io2RACmax) ) { okForRAC=true; max_storage_throughput = 1000*16; }
            if ( (rdsItems[bii].Name.substring(0,3)=="r5.") && (working_DiskSpace*1 < io2RACmax) ) { okForRAC=true; max_storage_throughput = 1000*16; }
            if (rdsItems[bii].Name.substring(0,4)=="r5b.") { okForRAC=true; max_storage_throughput = 4000*16; }
            if (rdsItems[bii].Name.substring(0,7)=="x2iedn.") { okForRAC=true; max_storage_throughput = 4000*16; }
            if (rdsItems[bii].Name.substring(0,7)=="r6in.") { okForRAC=true; max_storage_throughput = 4000*16; }
            
            //hide the tpc shapes for now
            if ( rdsItems[bii].Name.includes("tpc") ) { okForRAC=false; }
          }
          
          // skip over certain shapes in certain scenarios...
          
          
          
          //if rds_algo=se, then skip over any non-SE2 option.  Or if rds_algo=custom, then skip over any non-Custom
          if ( ((config.rds_type == "se") && (rdsItems[bii].OracleLIse2_OD == null)) ||
                  ((config.rds_type == "custom") && (rdsItems[bii].OracleCustomOD == null)) ||
                  ((config.rds_type == "rac") && (!okForRAC)  ) ) {
                    //todo: update this with official RAC pricing field when added
            // console.log("not a SE2 or custom shape. skip",rdsItems[bii].Name,config.rds_type,okForRAC);
          } else if (
                config.frozen_prefs.tshape_algo == "N" &&
                rdsItems[bii].Name.substring(0, 1) == "t"
              ) {
            // console.log("skipping T shape");
          } else if (
                config.frozen_prefs.extMemShape_algo == "N" &&
                rdsItems[bii].Name.includes("tpc")
              ) {
            // console.log("skipping RDS Extended Memory shape");
          } else if (
                doSkipRegex &&
                regex.test(rdsItems[bii].Name.toLowerCase())
          ) {      
            // console.log("skipping RDS shape based on regex");
          } else if (
                config.frozen_prefs.nvme_shape_algo == "Y" &&
                ( rdsItems[bii].InstanceStorageGB == undefined )
              ) {
              // console.log("skipping non-localNVME shape");
          // } else if (
          //       config.frozen_prefs.rds_baremetal_ranking == "never" &&
          //       rdsItems[bii].Name.includes("metal") 
          //     ) {
          //     // console.log("skipping bare metal when rds_baremetal_ranking=never");
          } else if (
                config.frozen_prefs.rds_baremetal_ranking == "always" &&
                !rdsItems[bii].Name.includes("metal") 
              ) {
              // console.log("skipping nopn-bare metal when rds_baremetal_ranking=always");
          } else if (config.UseRI=="SP-Only" && rdsItems[bii].Oracle1yrNUSP==null) {
            // if UseRI is SP-Only, then skip any shapes without SP pricing
            // NEED TO UPDATE THIS WHEN SE2 ADDS SAVINGS PLANS
            // console.log("not database savings plan shape. skip",rdsItems[bii].Name);
          } else
          {

            match_string = `db.${rdsItems[bii].Name} with ${rdsItems[bii].VCPU} VCPU${rdsItems[bii].Name.startsWith('t') ? " (bursting)" :""}${((typeof rdsItems[bii].OptimizeVCPUs != "undefined")&&(rdsItems[bii].OptimizeVCPUs != null)&&(rdsItems[bii].OptimizeVCPUs != "None")&& (rdsItems[bii].OptimizeVCPUs != "2")&&(config.optgoal_choice=="Y") && (config.rds_type!="se") && (config.rds_type!="custom") && (config.rds_type!="rac") ) ? " (OptimizeCPU eligible)" : ""}, ${rdsItems[bii].Memory} GB, ${rdsItems[bii].Throughput} MB/s${rdsItems[bii].BaselineThroughput!=null ? " (bursting)" :""}${(rdsItems[bii].Throughput*1>max_storage_throughput) ? " but storage maxes out at "+max_storage_throughput+" MB/s" : ""}, ${rdsItems[bii].IOPS} IOPS${rdsItems[bii].BaselineIOPS!=null ? " (bursting)" :""}`;
            
            // if we are not using IO bursting, then use baseline figures
            if ( (config.frozen_prefs.bursting_algo??'Y') == 'N' && rdsItems[bii].BaselineThroughput!=null) {
              match_string = `db.${rdsItems[bii].Name} with ${rdsItems[bii].VCPU} VCPU${rdsItems[bii].Name.startsWith('t') ? " (bursting)" :""}${((typeof rdsItems[bii].OptimizeVCPUs != "undefined")&&(rdsItems[bii].OptimizeVCPUs != null)&&(rdsItems[bii].OptimizeVCPUs != "None")&& (rdsItems[bii].OptimizeVCPUs != "2")&&(config.optgoal_choice=="Y") && (config.rds_type!="se") && (config.rds_type!="custom") && (config.rds_type!="rac") ) ? " (OptimizeCPU eligible)" : ""}, ${rdsItems[bii].Memory} GB, ${rdsItems[bii].BaselineThroughput} MB/s (baseline), ${rdsItems[bii].BaselineIOPS} IOPS (baseline)`;
            }

          if (config.frozen_prefs.nvme_shape_algo == "Y") {
            match_string += `, ${rdsItems[bii].InstanceStorage}`;
          }
    
            //Notice the Math.min(max_storage_throughput) below.  Will need to change this when we support io2BX
            if (
              rdsItems[bii].VCPU * 1 >= working_VCPU * 1 &&
              rdsItems[bii].Memory * 1 >= working_Memory * 1 &&
              Math.min(max_storage_throughput,( (config.frozen_prefs.bursting_algo??'Y') == 'N' && rdsItems[bii].BaselineThroughput!=null) ?  (rdsItems[bii].BaselineThroughput *1) : (rdsItems[bii].Throughput * 1) ) >= working_Throughput * 1 &&
              ( ( (config.frozen_prefs.bursting_algo??'Y') == 'N' && rdsItems[bii].BaselineIOPS!=null ) ?  (rdsItems[bii].BaselineIOPS *1) : (rdsItems[bii].IOPS * 1)  ) >= working_IOPS * 1
            ) {

               // check if we should skip this because it is metal and we aren't looking for metal
                // if min_license and never and Name.include=metal
                // then save the first match into bareMetalMatch
                if (
                  config.frozen_prefs.rds_baremetal_ranking == "never" &&
                  rdsItems[bii].Name.includes("metal") &&
                  config.optgoal_choice=="min_license"
                ) {
                  if (bareMetalMatch==null) {
                    // console.log("Found a metal shape, but not going to use it", rdsItems[bii].Name);
                    bareMetalMatch = rdsItems[bii];
                  }  
                } else {
   
                  //check if we should skip this because it does not support OptimizeCPU
                  if ( (config.optgoal_choice!="Y") || ((typeof rdsItems[bii].OptimizeVCPUs != "undefined")&&(rdsItems[bii].OptimizeVCPUs != null)&&(rdsItems[bii].OptimizeVCPUs != "None")) ) {
                    rdsItemSelected = rdsItems[bii];
                    // console.log("Submit: found rds match:"+rdsItems[bii].Name)
                    break;
                  } else {
                    //this shape does not use optimizeCPU.  are we close?
                    if ((rdsItems[bii].VCPU * 1 - working_VCPU * 1)<5) {
                        //close enough
                        rdsItemSelected = rdsItems[bii];
                        // console.log("Submit: found rds match:"+rdsItems[bii].Name+ " reqVCPU="+working_VCPU + "shapeVCPU="+rdsItems[bii].VCPU)
                        break;
                    }
                  }
                }  
                
                
            } else {
              //the shape was not selected.
              //also notice the Math.min(max_storage_throughput). address this when io2BX arrives
              let new_closeness = Math.min((rdsItems[bii].VCPU * 1 - working_VCPU * 1) / rdsItems[bii].VCPU,0) +
                Math.min((rdsItems[bii].Memory * 1 - working_Memory * 1 ) / rdsItems[bii].Memory,0) +
                Math.min((Math.min(max_storage_throughput,rdsItems[bii].Throughput * 1) - working_Throughput * 1 ) / Math.min(max_storage_throughput,rdsItems[bii].Throughput * 1),0) +
                Math.min((rdsItems[bii].IOPS * 1 - working_IOPS * 1) / rdsItems[bii].IOPS,0);
                
              // if we are not using IO bursting, then use baseline figures
              if ( (config.frozen_prefs.bursting_algo??'Y') == 'N' && rdsItems[bii].BaselineThroughput!=null) {
                  new_closeness = Math.min((rdsItems[bii].VCPU * 1 - working_VCPU * 1) / rdsItems[bii].VCPU,0) +
                Math.min((rdsItems[bii].Memory * 1 - working_Memory * 1 ) / rdsItems[bii].Memory,0) +
                Math.min((Math.min(max_storage_throughput,rdsItems[bii].BaselineThroughput * 1) - working_Throughput * 1 ) / Math.min(max_storage_throughput,rdsItems[bii].BaselineThroughput * 1),0) +
                Math.min((rdsItems[bii].BaselineIOPS * 1 - working_IOPS * 1) / rdsItems[bii].BaselineIOPS,0);
              }    
              
              if (new_closeness > closeness) {
                gap_memory=  Math.round( Math.max((working_Memory * 1 ) - rdsItems[bii].Memory * 1 ,0) ) ;
                gap_throughput=  Math.round( Math.max((working_Throughput * 1 ) - Math.min(max_storage_throughput,rdsItems[bii].Throughput * 1) ,0) ) ;
                gap_iops=  Math.round( Math.max((working_IOPS * 1 ) - rdsItems[bii].IOPS * 1 ,0) ) ;
                gap_vcpu=  Math.max((working_VCPU * 1 ) - rdsItems[bii].VCPU * 1 ,0) ;
                // console.log(rdsItems[bii].Name, new_closeness);
                // console.log("Next closest match:",rdsItems[bii].Name," ",rdsItems[bii].VCPU," ",rdsItems[bii].Memory," ",rdsItems[bii].Throughput," ",rdsItems[bii].IOPS);
                // closeness_string = `Next closest match: ${rdsItems[bii].Name} with ${rdsItems[bii].VCPU} VCPU${( ((typeof rdsItems[bii].OptimizeVCPUs != "undefined")&&(rdsItems[bii].OptimizeVCPUs != null)) && (rdsItems[bii].OptimizeVCPUs != "2") && (config.optgoal_choice=="Y") && (config.rds_type!="se") && ((rdsItems[bii].VCPU * 1 - working_VCPU * 1)>0)  ) ? " (OptimizeCPU eligible)" : ""}, ${rdsItems[bii].Memory} GB, ${rdsItems[bii].Throughput} MB/s, ${rdsItems[bii].IOPS} IOPS`;
                closeness_string = `Next closest match${ config.rds_type=="rac" ? " for "+divider+" node cluster" : ""}: db.${rdsItems[bii].Name} with ${rdsItems[bii].VCPU} VCPU${rdsItems[bii].Name.startsWith('t') ? " (bursting)" :""}${(gap_vcpu>0) ? " (short "+gap_vcpu.toFixed(1)+" VCPU)" : ""}${( ((typeof rdsItems[bii].OptimizeVCPUs != "undefined")&&(rdsItems[bii].OptimizeVCPUs != null)&&(rdsItems[bii].OptimizeVCPUs != "None")) && (rdsItems[bii].OptimizeVCPUs != "2") && (config.optgoal_choice=="Y") && (config.rds_type!="se") && (config.rds_type!="custom") && (config.rds_type!="rac") && ((rdsItems[bii].VCPU * 1 - working_VCPU * 1)>0)  ) ? " (OptimizeCPU eligible)" : ""}, ${rdsItems[bii].Memory} GB${(gap_memory>0) ? " (short "+gap_memory+" GB)" : ""}, ${rdsItems[bii].Throughput} MB/s${rdsItems[bii].BaselineThroughput!=null ? " (bursting)" :""}${(rdsItems[bii].Throughput*1>max_storage_throughput) ? " but storage maxes out at "+max_storage_throughput+" MB/s" : ""}${(gap_throughput>0) ? " (short "+gap_throughput+" MB/s)" : ""}, ${rdsItems[bii].IOPS} IOPS${rdsItems[bii].BaselineIOPS!=null ? " (bursting)" :""}${(gap_iops>0) ? " (short "+gap_iops+" IOPS)" : ""}`;
                
                // if we are not using IO bursting, then use baseline figures
                if ( (config.frozen_prefs.bursting_algo??'Y') == 'N' && rdsItems[bii].BaselineThroughput!=null) {
                  gap_throughput=  Math.round( Math.max((working_Throughput * 1 ) - Math.min(max_storage_throughput,rdsItems[bii].BaselineThroughput * 1) ,0) ) ;
                  gap_iops=  Math.round( Math.max((working_IOPS * 1 ) - rdsItems[bii].BaselineIOPS * 1 ,0) ) ;
                  closeness_string = `Next closest match${ config.rds_type=="rac" ? " for "+divider+" node cluster" : ""}: db.${rdsItems[bii].Name} with ${rdsItems[bii].VCPU} VCPU${rdsItems[bii].Name.startsWith('t') ? " (bursting)" :""}${(gap_vcpu>0) ? " (short "+gap_vcpu.toFixed(1)+" VCPU)" : ""}${( ((typeof rdsItems[bii].OptimizeVCPUs != "undefined")&&(rdsItems[bii].OptimizeVCPUs != null)&&(rdsItems[bii].OptimizeVCPUs != "None")) && (rdsItems[bii].OptimizeVCPUs != "2") && (config.optgoal_choice=="Y") && (config.rds_type!="se") && (config.rds_type!="custom") && (config.rds_type!="rac") && ((rdsItems[bii].VCPU * 1 - working_VCPU * 1)>0)  ) ? " (OptimizeCPU eligible)" : ""}, ${rdsItems[bii].Memory} GB${(gap_memory>0) ? " (short "+gap_memory+" GB)" : ""}, ${rdsItems[bii].BaselineThroughput} MB/s (baseline)${(gap_throughput>0) ? " (short "+gap_throughput+" MB/s)" : ""}, ${rdsItems[bii].BaselineIOPS} IOPS (baseline)${(gap_iops>0) ? " (short "+gap_iops+" IOPS)" : ""}`;
                }
                
                closeness=new_closeness;
              }
            } //end if selected
          
          } //end if rds_algo=se and not an SE shape
         }  // end if (rdsItems[bii].OracleOD *1 >0)
        } //end loop
      

      // console.log(match_string);
      // console.log("RDS", closeness_string);
      if (rdsItemSelected==null) {
        rdsItemSelected = { Name: null};
      }
      rdsItemSelected.closeness_string = closeness_string;
      rdsItemSelected.match_string = match_string;
      rdsItemSelected.gap_memory = gap_memory *  divider;
      rdsItemSelected.gap_throughput = gap_throughput *  divider;
      rdsItemSelected.gap_iops = gap_iops *  divider;
      rdsItemSelected.gap_vcpu = gap_vcpu *  divider;
      
      delete rdsItemSelected.bareMetalMatch;
      if (bareMetalMatch != null) {
        rdsItemSelected.bareMetalMatch = bareMetalMatch;
      }
    
      return rdsItemSelected;
    }








function   pick_rac_shape(regionInfo, config) {


// regionInfo = array of different RDS Shapes to consider
// config = details about the target values and other properties of the database


     var working_IOPS = config.IOPS;
     var working_VCPU = config.VCPU;
     var working_Memory = config.Memory;
     var working_Throughput = config.Throughput;
     var working_DiskSpace = config.DiskSpace;

      // set working_IOPS equal to config.AdjustedIOPS if it exists else set it to config.IOPS
      if (config.AdjustedIOPS) {
        working_IOPS = config.AdjustedIOPS;
      }
      if (config.AdjustedMemory) {
        working_Memory = config.AdjustedMemory;
        // console.log("pick_rds_shape", "Using AdjustedMemory", working_Memory);
        }
      if(config.AdjustedThroughput) {
         working_Throughput = config.AdjustedThroughput;
       }
      if (config.AdjustedVCPU) {
        working_VCPU = config.AdjustedVCPU;
      }

// optimizeCPU=Y doesn't make sense for rac
  if (config.optgoal_choice=='Y') {
    config.optgoal_choice='min_license';
  }

// create a disconnected copy of config
  let node_config = JSON.parse(JSON.stringify(config));
  
  let divider=2;
  let buffer=0;
  if (config.frozen_prefs.rac_buffer_algo=="10") buffer=0.1;
  if (config.frozen_prefs.rac_min_node_algo=="3") divider=3;

  let current_choice=2;
  // let next_choice=current_choice+1;
  let success=false;
  let individual_node={};
  
  let current_VCPU=9999;
  let current_Cost=9999;
  let winner=0;
  
  // make sure RDS Custom is offered in the region or elsewe can 't use it
  var rdsCustomAvail = false;
  // loop through regionInfo
  for (var bii = 0; bii < regionInfo.shapes.length; bii++) {
     if (regionInfo.shapes[bii].OracleCustomOD*1 > 0) {
       rdsCustomAvail = true;
     }
  }   
   
   
  // while (!success && (next_choice!="done") ) {
  while ( (current_choice<17) && rdsCustomAvail ) {
    
    divider = current_choice;
  
    // console.log("rac next divider buffer", current_choice, next_choice, divider, buffer);
    node_config.Memory = working_Memory * (1+buffer) / divider;
    node_config.VCPU = working_VCPU * (1+buffer)/ divider;
    node_config.Throughput = working_Throughput * (1+buffer)/ divider;
    node_config.IOPS = working_IOPS * (1+buffer)/ divider;
    node_config.divider = divider;
  
    // console.log("pick_rac_shape specs", config.database,  node_config.Memory, node_config.VCPU, node_config.Throughput,  node_config.IOPS,  node_config.divider, node_config);
    
    //make sure we only look at 
    individual_node = pick_rds_shape(regionInfo, node_config);
    
    if (individual_node.Name != null) {
      success=true;
      // console.log("pick_rac_shape success",divider,individual_node.match_string,individual_node.VCPU*divider,current_VCPU,individual_node.OracleOD*divider, current_Cost);

      if (config.optgoal_choice=="min_license") {
        // console.log("sort by VCPU");
        // if ( ( individual_node.VCPU*divider < current_VCPU ) || ( ( individual_node.VCPU*divider <= current_VCPU ) && ( individual_node.OracleOD*divider < (current_Cost-0.2) ) ) ) {
        // new logic.  if VCPU is less, then winner.  If VCPU is equal, then if divider is the same, pick the lower cost.  B
        var isBetter = false;
        if ( individual_node.VCPU*divider < current_VCPU ) isBetter = true;
        if ( ( individual_node.VCPU*divider == current_VCPU ) ) {
          // same VCPU.  So, is one cheaper?
          // console.log("pick_rac_shape maybe-VCPU", config.database, divider+" nodes",individual_node.match_string,"VCPU now "+individual_node.VCPU*divider+" was "+ current_VCPU,"Cost now "+individual_node.OracleOD*divider + " was " + current_Cost);
          if ( individual_node.OracleOD*divider < (current_Cost-0.2) ) isBetter=true;
          
        }
        if (isBetter) {
          // console.log("this is better");
          console.log("pick_rac_shape success-VCPU", config.database, divider+" nodes",individual_node.match_string,"VCPU now "+individual_node.VCPU*divider+" was "+ current_VCPU,"Cost now "+individual_node.OracleOD*divider + " was " + current_Cost);
          winner=divider;
          current_VCPU = individual_node.VCPU*divider;
          current_Cost = individual_node.OracleOD*divider;
        }
        
      } else  {
        
        // console.log("sort by Cost");
        // use round because there can be small variations at the cent level in r5b.  0.551 versus 0.55129.  If we multiple by 8 nodes that is 4.4103333 versus 4.408.  If we multiply by 2 that is 1.102 versus 1.10258.  We will multiply by 10 and then round
        if ( ( Math.round(individual_node.OracleOD*divider*10) < Math.round(current_Cost*10) ) || ( ( Math.round(individual_node.OracleOD*divider*10) <= Math.round(current_Cost*10) ) && ( individual_node.VCPU*divider < current_VCPU ) ) ) {
          // console.log("this is better");
          console.log("pick_rac_shape success-Cost", config.database, divider+" nodes",individual_node.match_string,"Cost now "+individual_node.OracleOD*divider + " was " + current_Cost, "VCPU now "+individual_node.VCPU*divider+" was "+ current_VCPU);
          winner=divider;
          current_VCPU = individual_node.VCPU*divider;
          current_Cost = individual_node.OracleOD*divider;
        }

      }  

      
      if ( ( individual_node.VCPU*divider <= current_VCPU ) && ( individual_node.OracleOD*divider < (current_Cost-1) ) ) {
        // console.log("this is better");
        winner=divider;
        current_VCPU = individual_node.VCPU*divider;
        current_Cost = individual_node.OracleOD*divider;
      }
      
      
    } else {
      
    }
    
    current_choice++;
    
  // console.log("pick_rac_shape",node_config,individual_node);
  }
  
  //if success, re-fetch individual node (I could perhaps save this)
  if (success) {
    divider = winner;
    node_config.divider = divider;
    node_config.Memory = working_Memory * (1+buffer) / divider;
    node_config.VCPU = working_VCPU * (1+buffer)/ divider;
    node_config.Throughput = working_Throughput * (1+buffer)/ divider;
    node_config.IOPS = working_IOPS * (1+buffer)/ divider;
  
    
    individual_node = pick_rds_shape(regionInfo, node_config);
    
  }
  
  config.rac_nodes=divider;
  
  return individual_node;


}





/*
Summary:
This function calculates and configures RDS (Relational Database Service) settings based on input parameters. It handles various database types, storage options, and pricing models. The function performs extensive calculations and validations to determine the optimal configuration and provides detailed cost estimates and performance metrics.

Description:

1. Input Processing:
   - Processes input parameters for region info, selected RDS item, and configuration details.
   - Sets working variables for IOPS, VCPU, Memory, Throughput, and DiskSpace.

2. Configuration Setup:
   - Initializes a configuration object with default values for various RDS settings.
   - Sets up RDS type description based on the input configuration.

3. Volume Type Selection:
   - Determines the appropriate volume type (gp2, gp3, io1, io2) based on performance requirements and regional availability.
   - Applies logic to switch between volume types if necessary to meet IOPS or throughput targets.

4. Performance Calculations:
   - Calculates IOPS, throughput, and storage requirements for the selected volume type.
   - Applies burst and baseline performance calculations where applicable.

5. Cost Estimation:
   - Calculates costs for compute resources, storage, and IOPS based on the selected instance type and storage configuration.
   - Handles different pricing models (On-Demand, Reserved Instances) and multi-AZ deployments.

6. Warning Generation:
   - Generates warnings for various scenarios such as exceeding IOPS limits, throughput limitations, or volume size constraints.

7. Summary Generation:
   - Creates detailed summaries of the selected configuration, including instance type, storage details, and estimated monthly costs.

8. Additional Calculations:
   - Performs calculations for "surplus" resources (e.g., additional VCPUs, memory).
   - Generates data for performance graphs (IOPS and throughput targets).

9. Final Configuration:
   - Assembles all calculated data into a final configuration object.
   - Includes suggestions for PostgreSQL conversion and Standard Edition 2 considerations.

Parameters:

1. regionInfo: Object containing information about the AWS region, including pricing and availability of various instance types and storage options.

2. rdsItemSelected: Object representing the selected RDS instance type, including details like VCPU, memory, and performance characteristics.

3. config: Object containing the initial configuration settings, including target IOPS, VCPU, memory, throughput, and disk space requirements. Also includes flags for various options like RDS type, volume type, and pricing model preferences.
*/



function   calculate_rds_config(regionInfo, rdsItemSelected, config) {

// **************************
// RDS Configuration
// **************************

// rdsItemSelected = a document contain specific rdsItem (shape) and its details, closeness_string, and match_string
// config = details about the target values and other properties of the database


     var working_IOPS = config.IOPS;
     var working_VCPU = config.VCPU;
     var working_Memory = config.Memory;
     var working_Throughput = config.Throughput;
     var working_DiskSpace = config.DiskSpace;

      // set working_IOPS equal to config.AdjustedIOPS if it exists else set it to config.IOPS
      if (config.AdjustedIOPS) {
        working_IOPS = config.AdjustedIOPS;
      }
      if (config.AdjustedMemory) {
        working_Memory = config.AdjustedMemory;
        // console.log("pick_rds_shape", "Using AdjustedMemory", working_Memory);
        }
      if(config.AdjustedThroughput) {
         working_Throughput = config.AdjustedThroughput;
       }
      if (config.AdjustedVCPU) {
        working_VCPU = config.AdjustedVCPU;
      }

    let rds_type_desc="RDS-Oracle (BYOL)";
    if (config.rds_type=="rac") rds_type_desc="RDS Custom for Oracle RAC";
    if (config.rds_type=="custom") rds_type_desc="RDS Custom for Oracle";
    if (config.rds_type=="se") rds_type_desc="RDS-Oracle SE2 (LI)";

        config.targetconfig_rds = {
          summaryText: "DBCSI's current logic is not able to auto-size this configuration for "+rds_type_desc+". Please reach out to your AWS Database Specialist for help.",
          rds_type_desc: rds_type_desc,
          monthly_estimate: "null",        
          shape: "null",
          shape_OD: "null",
          shape_RI: "null",
          shape_SP: "null",
          shape_RI_SE2: "null",
          shape_max_vcpu: "null",
          shape_max_vcpu_with_multiAZ: "null",
          shape_optimize_vcpu: "null",
          requested_vcpu: "null",
          shape_memory: "null",
          requested_memory: "null",
          shape_throughput: "null",
          requested_throughput: "null",
          requested_IOPS: "null",
          shape_summaryText: "",
          ebs_data_type: "null",
          ebs_data_size: "null",
          ebs_data_iops: "",
          ebs_data_summaryText1: "",
          ebs_data_summaryText2: "",
          ebs_data_cost: "",
          ebs_baseline_IOPS: "",
          ebs_burst_IOPS: "",
          ebs_burst_Throughput: "",
          ebs_baseline_Throughput: "",
          backup_style: "null",
          ebs_backup_type: "null",
          ebs_backup_size: "null",
          ebs_backup_iops: "",
          ebs_backup_snap_size: "",
          ebs_backup_summaryText1: "",
          ebs_backup_summaryText2: "",
          license_summaryText: "",
          comments: "",
          warnings: "",
          iops_warnings_alert: "",
          iops_warnings_burst: "",
          iops_warnings_info: "",
          UseMAZ: "No",
          closeness_string: rdsItemSelected.closeness_string,
          gap_memory: rdsItemSelected.gap_memory ,
          gap_throughput: rdsItemSelected.gap_throughput ,
          gap_iops: rdsItemSelected.gap_iops ,
          gap_vcpu: rdsItemSelected.gap_vcpu ,
          match_string: "",
          cpu_warnings_alert: "",
          cpu_warnings_burst: "",
          cpu_warnings_info: "",
          memory_warnings_alert: "",
          diskspace_warnings: "",
          iomb_warnings_info: "",
          iomb_warnings_burst: "",
          iomb_warnings_alert: "",
          ebsvol_warnings: "",
          rds_type: "ee"
        };            

      config.Comments='';
      
      if (config.diskspace_target_comments) {
        
        config.targetconfig_rds.diskspace_warnings = "["+config.diskspace_target_comments+"] ";
        // console.log("NOV25", config.targetconfig_rds.diskspace_warnings);
      }
      
// some checks for disk space too big
      
      // use EC2 if disk size is >64TB (or 256TB)
      var maxDiskSpace = 64000;
      var maxThroughput = 16000;
      if (config.rds_type!="custom" && config.frozen_prefs.asv_min_storage!=undefined && (config.frozen_prefs.asv_min_storage.trim()!='') && (config.frozen_prefs.asv_min_storage*1>0)) {
        maxDiskSpace = 256000;
        maxThroughput = 64000;
        // console.log("allow big storage");
      }
      if ((working_DiskSpace*1 > maxDiskSpace) && (config.rds_type!='rac') ) {
        config.TargetPlatform = 'EC2';
        config.Comments = config.Comments + "[Setting target to EC2 when disk space is > "+maxDiskSpace+" GB] ";
        if ((config.targetconfig_rds.diskspace_warnings==undefined) || (config.targetconfig_rds.diskspace_warnings.indexOf("Setting target to EC2 when disk space is")==-1)) {
          config.targetconfig_rds.diskspace_warnings = config.targetconfig_rds.diskspace_warnings + "[Setting target to EC2 when disk space is > "+maxDiskSpace+" GB] ";
        }  
      } else if ( (working_Throughput*1>maxThroughput) &&(config.rds_type!='rac') ) {
            // use EC2 if IO Throughput is >maxThroughput
        config.TargetPlatform = 'EC2';
        config.Comments = config.Comments + "[Setting target to EC2 when Throughput is > "+maxThroughput+" MB/s] ";
        if ((config.targetconfig_rds.iomb_warnings_alert==undefined) || (config.targetconfig_rds.iomb_warnings_alert.indexOf("Setting target to EC2 when Throughput is")==-1)) {
          config.targetconfig_rds.iomb_warnings_alert = config.targetconfig_rds.iomb_warnings_alert + "[Setting target to EC2 when Throughput is > "+maxThroughput+" MB/s] ";
        }
      }

      // if throughout is >4000, but we don't offer io2
      if ( (config.rds_type!="rac") && (working_Throughput*1>4000 ) && (working_Throughput*1<=16000 ) &&!(regionInfo.rds_io2_gb*1>0) ) {
          config.TargetPlatform = 'EC2';
          config.targetconfig_rds.iomb_warnings_alert = config.targetconfig_rds.iomb_warnings_alert + "[target Throughput is > 4000 MB/s, but io2 does not appear available in the " + regionInfo.region+ " region] ";

      }





      // do we need AddlStorageVolumes
      
      
      config.targetconfig_rds.AddlStorageVolumes=0;
      
      // only do this logic based on prefs 
      if (config.rds_type!="custom" && config.frozen_prefs.asv_min_storage!=undefined && (config.frozen_prefs.asv_min_storage.trim()!='') && (working_DiskSpace*1 > config.frozen_prefs.asv_min_storage*1)) {

        var ASVsBySpace = Math.ceil(working_DiskSpace/64000)-1;
        var ASVsByThroughput = Math.ceil(working_Throughput/4000)-1;
        var ASVsByIOPS = Math.ceil(working_IOPS/64000)-1;
        var ASVminsize = 200;
        var ASVsByMinGB = Math.max(0,Math.floor((working_DiskSpace-ASVminsize)/ASVminsize));
        
        // if ASVs>3, then switch to io2
        if (Math.max(ASVsBySpace, ASVsByThroughput, ASVsByIOPS) > 3) {
          console.log("Switching to io2 to try to meet disk requirements");
          config.targetconfig_rds.ebsvol_warnings = config.targetconfig_rds.ebsvol_warnings + "[Switching to io2 because it would take more than 3 Additional gp3 Storage Volumes(" + config.targetconfig_rds.AddlStorageVolumes + ") to meet disk requirements] ";
          config.VolumeType='io2';
        }
  
        // note: if using io2, recalculate ASVsByThroughput, ASVsByIOPS
        if ( ( (config.VolumeType!=undefined) && (config.VolumeType=='io2') ) || (config.frozen_prefs.ebs_algo=='io2') ) {
          ASVsBySpace = Math.ceil(working_DiskSpace/64000)-1;
          ASVsByThroughput = Math.ceil(working_Throughput/16000)-1;
          ASVsByIOPS = Math.ceil(working_IOPS/256000)-1;
        }  
        
        config.targetconfig_rds.AddlStorageVolumes=Math.min(ASVsByMinGB, Math.max(ASVsBySpace, ASVsByThroughput, ASVsByIOPS));
        // console.log("872- AdditionalStorageVolumes is ", config.targetconfig_rds.AddlStorageVolumes, config.VolumeType);
        
        // if ASVs>3, then we can't do this
        if (Math.max(ASVsBySpace, ASVsByThroughput, ASVsByIOPS) > 3) {
          console.log("Setting target to EC2 because it would take more than 3 Additional Storage Volumes(" + config.targetconfig_rds.AddlStorageVolumes + ") to meet disk requirements");
          config.targetconfig_rds.ebsvol_warnings = config.targetconfig_rds.ebsvol_warnings +  "[Setting target to EC2 because it would take more than 3 Additional Storage Volumes(" + config.targetconfig_rds.AddlStorageVolumes + ") to meet disk requirements] ";
          config.TargetPlatform = 'EC2';
          config.Comments = config.Comments + "[Setting target to EC2 because it would take more than 3 Additional Storage Volumes(" + config.targetconfig_rds.AddlStorageVolumes + ") to meet disk requirements] ";
        }
        
        
        // if we are using ASVs, make sure that each ASV is at least ASVminsize GB.  Raise a WARNING if not.
        // with ASVsByMinGB, we should not hit this
        if (config.targetconfig_rds.AddlStorageVolumes*1>0) {
          if (working_DiskSpace/(config.targetconfig_rds.AddlStorageVolumes+1)<ASVminsize) {
            console.log("To use Additional Storage Volumes, each ASV must be at least "+ ASVminsize +" GB");
            config.targetconfig_rds.ebsvol_warnings =
              config.targetconfig_rds.ebsvol_warnings +
                    "[Your configuration looks to need "+config.targetconfig_rds.AddlStorageVolumes +
                    " Additional Storage Volumes, which would suggest each ASV is "+Math.ceil(working_DiskSpace/(1+config.targetconfig_rds.AddlStorageVolumes)) +
                    " GB.  However, each ASV must be at least "+ ASVminsize +" GB] ";    
            // TODO-ASV: do I just raise working_DiskSpace to  ASVminsize*ASV ?        
          }
        }

        // if we are using ASVs, make sure instance memory is >= 64GB.  Raise a WARNING if not.
        // console.log("ASV-897", rdsItemSelected.Memory);
        if (config.targetconfig_rds.AddlStorageVolumes*1>0) {
          if (rdsItemSelected.Memory*1<64) {
            console.log("To use Additional Storage Volumes, memory must be at least 64GB");
            config.targetconfig_rds.memory_warnings_alert =
              config.targetconfig_rds.memory_warnings_alert +
                    "[The RDS shape does not have at least 64GB of memory, which is a requirement for Additional Storage Volumes] ";          
          }
        }
        
      }
      
      
      
      
      
      
       // Pick the VolumeType choice
       
      
      // console.log("io2-aa", config.VolumeType, working_Throughput, regionInfo.rds_io2_gb*1);
      if ( (config.VolumeType=='io2') && !(regionInfo.rds_io2_gb*1>0) ) {
        config.VolumeType=''; //don't use io2 if not available in region
        config.targetconfig_rds.iomb_warnings_alert = config.targetconfig_rds.iomb_warnings_alert + "[io2 requested, but io2 does not appear available in the " + regionInfo.region+ " region] ";
      }
       

      if ( (config.VolumeType==undefined) || (config.VolumeType=='') ) {
        var useASV = (config.targetconfig_rds.AddlStorageVolumes*1>0);
        
        if (config.frozen_prefs.ebs_algo=='gp2') config.VolumeType='gp2';
        if (config.frozen_prefs.ebs_algo=='gp3') config.VolumeType='gp3';
        if (config.frozen_prefs.ebs_algo=='io1') config.VolumeType='io1';
        if (config.frozen_prefs.ebs_algo=='io2') config.VolumeType='io2';

        if (useASV && (config.frozen_prefs.ebs_algo=='io1')) {
          config.VolumeType='io2';
          console.log("Cannot use io1 with Additional Storage Volumes");
          config.targetconfig_rds.ebsvol_warnings = config.targetconfig_rds.ebsvol_warnings + "[Cannot use io1 with Additional Storage Volumes] ";
        }  
        if (useASV && (config.frozen_prefs.ebs_algo=='gp2')) {
          config.VolumeType='gp3';
          console.log("Cannot use io1 with Additional Storage Volumes");
          config.targetconfig_rds.ebsvol_warnings = config.targetconfig_rds.ebsvol_warnings + "[Cannot use gp2 with Additional Storage Volumes] ";
        }
        
        
        // If using ASV, temporarily treat working_ values as a per-volume total.  Reset them later.
        const orig_working_DiskSpace = working_DiskSpace;
        const orig_working_IOPS = working_IOPS;
        const orig_working_Throughput = working_Throughput;
        if (useASV) {
          working_DiskSpace = Math.ceil(working_DiskSpace / (config.targetconfig_rds.AddlStorageVolumes+1));
          working_IOPS = Math.ceil(working_IOPS / (config.targetconfig_rds.AddlStorageVolumes+1));
          working_Throughput = Math.ceil(working_Throughput / (config.targetconfig_rds.AddlStorageVolumes+1));
        }


        if (config.frozen_prefs.ebs_algo=='p-gp') {
          //Prefer gp2 or gp3
            config.VolumeType='gp3'; //default gp3 for now
            
          

          
          
          //some of the logic below is probably redundant
          //sweet spot for gp2: diskspace is > 334*4 (get full 250MB/s per gp2)
          // and throughput < 1000 (where gp3 works)
          // and iops < diskspace*3 (where gp2 works)
          // and diskspace*3 > 12000 (where gp2 gives you more iops than gp3)
          if ( !useASV && (working_DiskSpace*1>=334*4) && (working_DiskSpace*3 > 12000) && (working_IOPS*1 < working_DiskSpace*3) &&  (working_Throughput*1<=1000) ) {
            config.VolumeType='gp2';
            // console.log("975-pick gp2", working_DiskSpace, working_IOPS, working_Throughput);
          }
          
          
          // if iops is > 64000, then io1
          if ( (config.rds_type!="rac") && (working_IOPS*1>64000) ) {
            if (regionInfo.rds_io2_gb*1>0) {
              config.VolumeType='io2';
              config.targetconfig_rds.ebsvol_warnings = config.targetconfig_rds.ebsvol_warnings + "[switching to io2 to meet target IOPS > 64000] ";
            } else {
              config.VolumeType='io1';
              config.targetconfig_rds.ebsvol_warnings = config.targetconfig_rds.ebsvol_warnings + "[switching to io1 to meet target IOPS > 64000] ";
            }
          }
          
          // if throughout is >4000, then io2
          if ( (config.rds_type!="rac") && (working_Throughput*1>4000 ) ) {
            if (regionInfo.rds_io2_gb*1>0) {
              config.VolumeType='io2';
              config.targetconfig_rds.ebsvol_warnings = config.targetconfig_rds.ebsvol_warnings + "[switching to io2 to meet target Throughput > 4000 MB/s] ";
            } else {
              // console.log("io2-b", working_Throughput, regionInfo.rds_io2_gb*1);
              config.TargetPlatform = 'EC2';
              config.targetconfig_rds.iomb_warnings_alert = config.targetconfig_rds.iomb_warnings_alert + "[target Throughput is > 4000 MB/s, but io2 does not appear available in the " + regionInfo.region+ " region] ";
            }
          }



          // if throughput is >1000, then gp3
          if (working_Throughput*1>1000 && config.VolumeType=='gp2') {
            config.VolumeType='gp3';
            //config.targetconfig_rds.ebsvol_warnings = config.targetconfig_rds.ebsvol_warnings + "[switching to io1 to meet target Throughput > 1000 MB/s] ";
          }
          
          // if we are at gp2 but IOPS and Throughput are < 12000,500, then use gp3 
          if (config.VolumeType=='gp2') {
            if ( (working_DiskSpace*1<200) && (working_IOPS*1 < 3000) && (working_Throughput*1 < 125) ) {
              config.VolumeType='gp3';
            }
             if ( (working_DiskSpace*1>=200) && (working_IOPS*1 < 12000) && (working_Throughput*1 < 500) ) {
              config.VolumeType='gp3';
            }
         }  
          if (config.VolumeType=='gp2') {
            if (working_DiskSpace*1<200) {
              //assume 1 volume for now...
        
              let burst_IOPS = 3000;
              
              if ((working_IOPS*1 > burst_IOPS) ) {
                
                //can we grow the EBS volumes to split to 4 volumes and meet the target
                //4 volumes has 3000*4 burst.
                let new_DiskSpace = 200;
                burst_IOPS = 12000;
                if ((working_IOPS*1 <= burst_IOPS) ) {
                  // config.targetconfig_rds.diskspace_warnings = config.targetconfig_rds.diskspace_warnings + "[increasing gp2 disk space from "+working_DiskSpace+" to "+new_DiskSpace.toFixed(0)+" to get more IOPS] ";
                  // working_DiskSpace= new_DiskSpace.toFixed(0);
                  config.VolumeType='gp3';
                  // config.targetconfig_rds.ebsvol_warnings = config.targetconfig_rds.ebsvol_warnings + "[switching to gp3 to meet target IOPS] ";
                }  else {  
                  config.VolumeType='gp3';
                  // config.targetconfig_rds.ebsvol_warnings = config.targetconfig_rds.ebsvol_warnings + "[switching to gp3 to meet target IOPS] ";
                }  
              }
            } else {
            
              
              //assume 4 volumes for now...
              let baseline_IOPS = Math.min(working_DiskSpace/4 * 3, 16000)*4;
              let burst_IOPS = Math.max(3000*4,baseline_IOPS);
              
              if ((working_IOPS*1 > burst_IOPS) && (config.VolumeType=='gp2')) {
                
                //can we grow the EBS volumes to meet the target?  We will allow doubling of gp2 size to try to match
                //
                let new_DiskSpace = working_IOPS/3;
                // 5333*4 is the diskspace max for gp2 IOPS
                if ((new_DiskSpace < 5333*4) ) {
                  // config.targetconfig_rds.diskspace_warnings = config.targetconfig_rds.diskspace_warnings + "[increasing gp2 disk space from "+working_DiskSpace+" to "+new_DiskSpace.toFixed(0)+" to get more IOPS] ";
                  // working_DiskSpace= new_DiskSpace.toFixed(0);
                  config.VolumeType='gp3';
                  // config.targetconfig_rds.ebsvol_warnings = config.targetconfig_rds.ebsvol_warnings + "[switching to gp3 to meet target IOPS] ";
                } else {
                  config.VolumeType='gp3';
                  // config.targetconfig_rds.ebsvol_warnings = config.targetconfig_rds.ebsvol_warnings + "[switching to gp3 to meet target IOPS] ";
                }  
              }
            }  
          }
            
        }
        
        // If using ASV, we temporarily treaedt working_ values as a per-volume total.  Reset them now.
        if (useASV) {
          working_DiskSpace = orig_working_DiskSpace;
          working_IOPS = orig_working_IOPS;
          working_Throughput = orig_working_Throughput;
        }  

        
      } else {
        // console.log("volumeType is",config.VolumeType);
      }// end Pick Volume Type section
      
       


       
     // some volume types dont work with  RAC
      if (config.rds_type=='rac') {
        config.VolumeType='io2';
      }
     
     // set some minimums
     
      if (config.VolumeType=='gp2') {
        if (working_DiskSpace*1 < 20) {
          working_DiskSpace='20';
          config.targetconfig_rds.ebsvol_warnings =
            config.targetconfig_rds.ebsvol_warnings + "[using gp2 minimum disk space of 20GB for pricing.] ";
        }
      } else if (config.VolumeType=='gp3') {
        if (working_DiskSpace*1 < 20) {
          working_DiskSpace='20';
                    config.targetconfig_rds.ebsvol_warnings =
            config.targetconfig_rds.ebsvol_warnings + "[using gp3 minimum disk space of 20GB for pricing.] ";

        }
      } else if (config.VolumeType=='io1') {
        if (working_DiskSpace*1 < 100) {
          working_DiskSpace='100';
                    config.targetconfig_rds.ebsvol_warnings =
            config.targetconfig_rds.ebsvol_warnings + "[using io1 minimum disk space of 100GB for pricing.] ";

        }
      } else if (config.VolumeType=='io2') {
        if (working_DiskSpace*1 < 100) {
          working_DiskSpace='100';
                    config.targetconfig_rds.ebsvol_warnings =
            config.targetconfig_rds.ebsvol_warnings + "[using io2 minimum disk space of 100GB for pricing.] ";

        }
      }


    
      if (config.VolumeType=='io1') {
        if (working_DiskSpace*0.5 > working_IOPS*1) {
          // assuming we will fit in RDS, then make IOPS be a minimum of 0.5*diskspace
          if (working_DiskSpace*1 < 64000 && working_Throughput*1<=4000) {
            config.targetconfig_rds.ebsvol_warnings = config.targetconfig_rds.ebsvol_warnings + "[RDS has a minimum io1 IOPS value of 0.5*the disk space in GB.  Increasing io1 iops from "+working_IOPS+" to be at least 1/2 disk space in GB ("+working_DiskSpace+" GB)] ";
            working_IOPS=(working_DiskSpace*0.5+1).toFixed(0);
          }  
        }
      }

      // gp3: we can only provision IOPS >3000 or Throughput>125 if the storage size is >200GB
      if ( (config.VolumeType=='gp3') && ( (working_IOPS*1>3000) || (working_Throughput*1 > 125) ) && (working_DiskSpace*1 < 200) ) {
        config.targetconfig_rds.ebsvol_warnings =
          config.targetconfig_rds.ebsvol_warnings + "[increased gp3 disk space from "+working_DiskSpace+"GB to 200GB in order to provision extra IOPS or Throughput.] ";
        working_DiskSpace = 200;  
      }
      

      
            //TODO hard code backup for now
      config.BackupType = 'none';
      config.Backup = '0';
      config.SnapshotDays = '1';


      if ((rdsItemSelected.Name == null)||(config.TargetPlatform == 'EC2')) {
        // console.log("Submit: No matching RDS found for "+ config.database);
        
        
        // Step 2a- This is not a fit for RDS, so respond accordingly
        
        
        
        config.rds_shape = "N/A";
        config.extraInfo = "";
        config.extraInfo2 = "";
        config.considerSE2 = "";
        // config.current_hw_cpu = "";
        config.addlMaxVCPU = "";
        config.addlOptimVCPU = "";
        // config.current_hw_memory = "";
        config.addlMemory = "";
        config.addlThroughput = "";
        config.addlBaselineThroughput = "";
        config.addlBaselineIOPS = "";
        config.addlBurstIOPS = "";
        config.addlVolumes = "";
        config.RDSbenefits = "";

        // Clear target label/value fields so stale values from prior what-if calculations don't persist
        config.OrigthroughputtargetLabels = "Target";
        config.OrigthroughputtargetValues = "" + (config.OrigThroughput || config.Throughput || "");
        config.ThroughputtargetLabels = "Target";
        config.ThroughputtargetValues = "" + (config.Throughput || "");
        config.OrigIOPStargetLabels = "Target";
        config.OrigIOPStargetValues = "" + (config.OrigIOPS || config.IOPS || "");
        config.IOPStargetLabels = "Target";
        config.IOPStargetValues = "" + (config.IOPS || "");
        
        config.targetconfig_rds.warnings = "No matching shape found";



      } else
      {


        // Step 2b - This is a fit for RDS.  So, describe it
        
        
        // this is the old targetconfig_rds code...
      var tempConfig = config.targetconfig_rds;
      
  
      if (config.rds_type != "rac") config.rac_nodes=1;

      tempConfig.shape = rdsItemSelected.Name;
      tempConfig.shape_OD = rdsItemSelected.OracleOD;
      tempConfig.shape_RI = rdsItemSelected.Oracle1yrNURI;
      tempConfig.shape_SP = rdsItemSelected.Oracle1yrNUSP;
      tempConfig.shape_RI_SE2 = rdsItemSelected.OracleLIse2_1yrNURI;
      tempConfig.shape_max_vcpu = rdsItemSelected.VCPU;
      tempConfig.shape_optimize_vcpu = rdsItemSelected.VCPU;
      tempConfig.requested_vcpu = working_VCPU;
      tempConfig.requested_memory = working_Memory;
      tempConfig.shape_memory = rdsItemSelected.Memory;
      tempConfig.requested_throughput = working_Throughput;
      tempConfig.requested_IOPS = working_IOPS;      
      tempConfig.shape_throughput = rdsItemSelected.Throughput;
      tempConfig.shape_baselinethroughput = rdsItemSelected.BaselineThroughput;
      tempConfig.shape_baselineIOPS= rdsItemSelected.BaselineIOPS;        
      tempConfig.shape_IOPS= rdsItemSelected.IOPS;        
      tempConfig.ebs_data_type = config.VolumeType;
      tempConfig.ebs_data_size = working_DiskSpace;
      tempConfig.rds_type = config.rds_type;
      tempConfig.rac_nodes = config.rac_nodes;
      
      if (typeof tempConfig.comments === "undefined") {
        tempConfig.comments = "";
      }

      // add some fields if we want to suggest a BareMetalPossibility
      if (rdsItemSelected.bareMetalMatch) {
         
         tempConfig.BareMetalPossibilityComments = "A bare metal instance of db."+ rdsItemSelected.bareMetalMatch.Name + " with " + (rdsItemSelected.bareMetalMatch.VCPU/vcpus_per_core(rdsItemSelected.bareMetalMatch)) + " cores (" + rdsItemSelected.bareMetalMatch.Processor + ") may be more license efficient (if you assume 0.5 core factor applies) for some customers than the proposed " + rdsItemSelected.VCPU + " VCPU db."+rdsItemSelected.Name+".  You can use the RDS Bare Metal Evaluation Preference drop-down to explore using your licensing preferences.";
         tempConfig.BareMetalPossibilityName =rdsItemSelected.bareMetalMatch.Name;
         tempConfig.BareMetalPossibilityVCPU =rdsItemSelected.bareMetalMatch.VCPU;
         tempConfig.BareMetalPossibilityProcessor =rdsItemSelected.bareMetalMatch.Processor;
         tempConfig.BareMetalPossibilityOracleCoreFactorString =rdsItemSelected.bareMetalMatch.OracleCoreFactorString;
      }
      
      // add some fields if this is a BareMetal shape
      if (rdsItemSelected.Name.includes("metal")) {
        tempConfig.BareMetalProcessor = rdsItemSelected.Processor;
        tempConfig.BareMetalOracleCoreFactorString = rdsItemSelected.OracleCoreFactorString;
      }
      
      tempConfig.UseMAZ = config.UseMAZ;
      // July 2025 - we now offer SE2-LI for x2iedn.4xlarge but only for MAZ.  So, we force MAZ if we are using SE2 and x2iedn.4xlarge
      if ( (tempConfig.shape=="x2iedn.4xlarge") && (tempConfig.rds_type=="se") && (tempConfig.UseMAZ!="Yes") ) {
        console.log("Forcing use of MAZ for SE2 and x2iedn.4xlarge");
        tempConfig.UseMAZ = "Yes";
        config.Comments = config.Comments + "[required use of MAZ when using SE2-LI and x2iedn.4xlarge] ";
      }

      
      if (rdsItemSelected.InstanceStorageGB != undefined) {
        tempConfig.InstanceStorageGB = rdsItemSelected.InstanceStorageGB;
      }  
      tempConfig.InstanceStorage = rdsItemSelected.InstanceStorage;
        

      if ( (1 * tempConfig.requested_vcpu > 1 * tempConfig.shape_max_vcpu) && (tempConfig.rds_type!="rac") ){
        tempConfig.cpu_warnings_alert =
          tempConfig.cpu_warnings_alert +
          "[The shape " +
          tempConfig.shape +
          " ( " + tempConfig.shape_max_vcpu + " VCPUs )" +
          " does not have " +
          tempConfig.requested_vcpu +
          " VCPUs.] ";
      }
      if ( (1 * tempConfig.requested_vcpu > 1 * tempConfig.shape_max_vcpu * tempConfig.rac_nodes) && (tempConfig.rds_type=="rac") )
            {
        tempConfig.cpu_warnings_alert =
          tempConfig.cpu_warnings_alert +
                "[The cluster of " +
                tempConfig.rac_nodes +
                " nodes of " +
                tempConfig.shape +
                " ( " + tempConfig.shape_max_vcpu + " VCPUs per node )" +
          " does not have " +
          tempConfig.requested_vcpu +
          " VCPUs.] ";
            }      
      if (tempConfig.shape.startsWith('t') ) {
        tempConfig.cpu_warnings_burst =
          tempConfig.cpu_warnings_burst +
          "[The shape " +
          tempConfig.shape +
          " uses burstable CPUs.  Be sure this meets your requirements and be aware that these shapes are configured in Unlimited mode (which can cause extra costs).] ";
      }

      
      if ( (1 * tempConfig.requested_memory > 1 * tempConfig.shape_memory)  && (tempConfig.rds_type!="rac") ) {
        tempConfig.memory_warnings_alert =
          tempConfig.memory_warnings_alert +
          "[The shape " +
          tempConfig.shape +
          " ( " + tempConfig.shape_memory + " GB )" +
          " does not have " +
          tempConfig.requested_memory +
          " GB of memory.] ";
      }
      if ( (1 * tempConfig.requested_memory > 1 * tempConfig.shape_memory * tempConfig.rac_nodes) && (tempConfig.rds_type=="rac") )
            {
        tempConfig.memory_warnings_alert =
          tempConfig.memory_warnings_alert +
                "[The cluster of " +
                tempConfig.rac_nodes +
                " nodes of " +
                tempConfig.shape +
                " ( " + tempConfig.shape_memory + " GB per node )" +
          " does not have " +
          tempConfig.requested_memory +
          " GB of memory.] ";
            }      
      if ( (
        1 * tempConfig.requested_throughput >
        1 * tempConfig.shape_throughput
      )  && (tempConfig.rds_type!="rac") ) {
        tempConfig.iomb_warnings_alert =
          tempConfig.iomb_warnings_alert +
          "[The shape " +
          tempConfig.shape +
          " ( " + tempConfig.shape_throughput + " MB/s )" +
          " does not support " +
          tempConfig.requested_throughput +
          " MB/s of IO throughput.] ";
      } else         if ( (1 * tempConfig.shape_baselinethroughput > 0   && (
          1 * tempConfig.requested_throughput >
          1 * tempConfig.shape_baselinethroughput)
        ) && (tempConfig.rds_type!="rac") )
        {
          tempConfig.iomb_warnings_burst =
            tempConfig.iomb_warnings_burst +
            "[The shape " +
            tempConfig.shape +
            " can burst for short periods to the requested " +
            tempConfig.requested_throughput +
            " MB/s of IO throughput, but has a baseline throughput of " + tempConfig.shape_baselinethroughput + " MB/s.] ";
        } 


      if ( (
        1 * tempConfig.requested_throughput >
        1 * tempConfig.shape_throughput * tempConfig.rac_nodes
      )  && (tempConfig.rds_type=="rac") ) {
        tempConfig.iomb_warnings_alert =
          tempConfig.iomb_warnings_alert +
                "[The cluster of " +
                tempConfig.rac_nodes +
                " nodes of " +
                tempConfig.shape +
                " ( " + tempConfig.shape_throughput + " MB/s per node )" +
          " does not support " +
          tempConfig.requested_throughput +
          " MB/s of IO throughput.] ";
      } else          if ( (1 * tempConfig.shape_baselinethroughput * tempConfig.rac_nodes > 0   && (
            1 * tempConfig.requested_throughput >
            1 * tempConfig.shape_baselinethroughput * tempConfig.rac_nodes)
          ) && (tempConfig.rds_type=="rac") )
            {
              tempConfig.iomb_warnings_burst =
                tempConfig.iomb_warnings_burst +
                "[The cluster of " +
                tempConfig.rac_nodes +
                " nodes of " +
                tempConfig.shape +
                " can burst for short periods to the requested " +
                tempConfig.requested_throughput +
                " MB/s of IO throughput, but has a baseline throughput of " + tempConfig.shape_baselinethroughput*tempConfig.rac_nodes + " MB/s.] ";
            }
        
        
      if ( (
        1 * tempConfig.requested_IOPS >
        1 * tempConfig.shape_IOPS
      )  && (tempConfig.rds_type!="rac") ) {
        tempConfig.iops_warnings_alert =
          tempConfig.iops_warnings_alert +
          "[The shape " +
          tempConfig.shape +
          " ( " + tempConfig.shape_IOPS + " IOPS )" +
          " does not support " +
          tempConfig.requested_IOPS +
          " IOPS.] ";
      } else         if ( (1 * tempConfig.shape_baselineIOPS > 0   && (
          1 * tempConfig.requested_IOPS >
          1 * tempConfig.shape_baselineIOPS)
        ) && (tempConfig.rds_type!="rac") )
        {
          tempConfig.iops_warnings_burst =
            tempConfig.iops_warnings_burst +
            "[The shape " +
            tempConfig.shape +
            " can burst for short periods to the requested " +
            tempConfig.requested_IOPS +
            " IOPS, but has a baseline IOPS of " + tempConfig.shape_baselineIOPS + ".] ";
        } 

      if ( (
        1 * tempConfig.requested_IOPS >
        1 * tempConfig.shape_IOPS * tempConfig.rac_nodes
      )  && (tempConfig.rds_type=="rac") ) {
        tempConfig.iops_warnings_alert =
          tempConfig.iops_warnings_alert +
                "[The cluster of " +
                tempConfig.rac_nodes +
                " nodes of " +
          tempConfig.shape +
          " ( " + tempConfig.shape_IOPS + " IOPS per node )" +
          " does not support " +
          tempConfig.requested_IOPS +
          " IOPS.] ";
      } else          if ( (1 * tempConfig.shape_baselineIOPS * tempConfig.rac_nodes > 0   && (
            1 * tempConfig.requested_IOPS >
            1 * tempConfig.shape_baselineIOPS * tempConfig.rac_nodes)
          ) && (tempConfig.rds_type=="rac") )
            {
              tempConfig.iops_warnings_burst =
                tempConfig.iops_warnings_burst +
                "[The cluster of " +
                tempConfig.rac_nodes +
                " nodes of " +
                tempConfig.shape +
                " can burst for short periods to the requested " +
                tempConfig.requested_IOPS +
                " IOPS, but has a baseline IOPS of " + tempConfig.shape_baselineIOPS*tempConfig.rac_nodes + ".] ";
            }
        
        
     
      

      //
      //
      // Compute/Shape Section
      //
      //

      if (tempConfig.rds_type=="se") {
        tempConfig.summaryText =
        "RDS-OracleSE2(License Included)";
      } else  if (tempConfig.rds_type=="custom") {
        tempConfig.summaryText =
        "RDS-Custom for Oracle(BYOL)";
      } else  if (tempConfig.rds_type=="rac") {
        tempConfig.summaryText =
        "RDS-Custom for Oracle Real Application Clusters(BYOL)";
      } else {
        tempConfig.summaryText =
        "RDS-OracleEE(BYOL)";
      }
      
      //optimizeVCPU calculation
      
      //skip if optgoal_choice!=Y or if SE2 or Custom or RAC (since they don't offer it)
      if (!((config.optgoal_choice=="min_license") || (config.optgoal_choice=="min_cost") || (tempConfig.rds_type=="se") || (tempConfig.rds_type=="custom") || (tempConfig.rds_type=="rac") )) {
        
        // does this shape offer optimizeVCPU?
        if ((typeof rdsItemSelected.OptimizeVCPUs != "undefined")&&(rdsItemSelected.OptimizeVCPUs != null)) {      
          //find the closest match...
          var choices = rdsItemSelected.OptimizeVCPUs.split(",");
          for (var ov = 0; ov < choices.length; ov++) {
            if (1 * choices[ov] >= tempConfig.requested_vcpu) {
              tempConfig.shape_optimize_vcpu = choices[ov];
              if (tempConfig.shape_optimize_vcpu*1 < tempConfig.requested_vcpu * 1) {
                tempConfig.cpu_warnings_info = tempConfig.cpu_warnings_info + "[Optimize CPU feature considered for licensing benefits.] ";  
              }  
              // console.log("Using optimizevcpu"+choices[ov])
              break;
            }
          }
        }
      
      } else {
        tempConfig.shape_optimize_vcpu = tempConfig.shape_max_vcpu;
      }

      //licenseText and Pricing
     var shapeCost = 0;
     var RIused = "OD";
     
     // console.log("UseRI", config.UseRI, tempConfig);
     
  // Update UseRI based on the selectedItem
     var temp_UseRI = config.UseRI;
     if (config.UseRI=="RI+SP") {
       // did we pick RI or SP?
       // default to RI
       temp_UseRI='RI';
       // but switch to SP if it exists and is less than RI
       // need to tweak this when Savings Plans supports SE2
       if (tempConfig.rds_type=="ee" && tempConfig.shape_SP!=null && (tempConfig.shape_RI == null || tempConfig.shape_RI*1>tempConfig.shape_SP*1) ) {
         // console.log("RI+SP switching to SP");
         temp_UseRI="SP";
       } else {
         // console.log("RI+SP NOT switching to SP");
       }
     }
  
     if (config.UseRI=="SP-Only") {
       temp_UseRI='SP';
     }
       
     
     if (tempConfig.rds_type=="se") {
      //License Included
      // Does not yet support Savings Plan
    
          tempConfig.license_summaryText =
            "SE2 License Included";  
    
          if (tempConfig.UseMAZ=="Yes") {
            
            if ((temp_UseRI=='RI') && tempConfig.shape_RI_SE2 > 0) {
              shapeCost = rdsItemSelected.OracleLIse2_1yrNURI * 730 *2;
              tempConfig.shape_summaryText =
                "RDS Compute Monthly estimate using MultiAZ (using 1-year Reserved Instance pricing): $" +
                shapeCost.toFixed(2);
              RIused="RI";  
            } else {
              // onDemand pricing for SE2 LI 
              shapeCost = rdsItemSelected.OracleLIse2_OD * 730 *2;
      
              tempConfig.shape_summaryText =
                "RDS Compute Monthly estimate using MultiAZ (using On-Demand pricing): $" +
                shapeCost.toFixed(2);
            }
            
           } else {
            if ((temp_UseRI=='RI') && tempConfig.shape_RI_SE2 > 0) {
              shapeCost = rdsItemSelected.OracleLIse2_1yrNURI * 730;
              RIused="RI"; 
              tempConfig.shape_summaryText =
                "RDS Compute Monthly SingleAZ estimate (using 1-year Reserved Instance pricing): $" +
                shapeCost.toFixed(2);
            } else {
              shapeCost = rdsItemSelected.OracleLIse2_OD * 730;
      
              tempConfig.shape_summaryText =
                "RDS Compute Monthly SingleAZ estimate (using On-Demand pricing): $" +
                shapeCost.toFixed(2);
            }
          }
          tempConfig.shape_cost = shapeCost;
          
     } else if (tempConfig.rds_type=="custom") {
      //Custom
    
          // is this bare metal?
          if (tempConfig.BareMetalProcessor) {
            // yes bare metal
              tempConfig.license_summaryText =
                "Oracle BYOL for db." +
                tempConfig.shape + " [ " +
                tempConfig.shape_max_vcpu/2 + " " +
                tempConfig.BareMetalProcessor+" Cores";
              if (tempConfig.UseMAZ=="Yes") {
                tempConfig.license_summaryText +=
                " per node. " + 1 * tempConfig.shape_max_vcpu  +
                " Cores total to account for both nodes of MultiAZ. ]";
              } else {
                tempConfig.license_summaryText += " ]";
              }  
          } else {
            // not bare metal
              tempConfig.license_summaryText =
                "Oracle BYOL for " +
                tempConfig.shape_optimize_vcpu +
                " VCPUs ";
              if (tempConfig.UseMAZ=="Yes") {
                tempConfig.license_summaryText +=
                "per node (" + 1 * tempConfig.shape_optimize_vcpu +
                " Oracle Processor Licenses total - accounts for licensing both nodes of MultiAZ)";
              } else {
                tempConfig.license_summaryText +=
                "("+ 0.5 * tempConfig.shape_optimize_vcpu +
                " Oracle Processor Licenses)";
              }  
        
           }
    
          if (tempConfig.UseMAZ=="Yes") {
            
            if  ((temp_UseRI=='RI') && (rdsItemSelected.OracleCustom1yrNURI*1 > 0) ) {
              shapeCost = rdsItemSelected.OracleCustom1yrNURI * 730 *2;
              RIused="RI"; 
              tempConfig.shape_summaryText =
                "RDS Compute Monthly estimate using MultiAZ (using 1-year Reserved Instance pricing): $" +
                shapeCost.toFixed(2);
            } else {
              // onDemand pricing for SE2 LI 
              shapeCost = rdsItemSelected.OracleCustomOD * 730 *2;
      
              tempConfig.shape_summaryText =
                "RDS Compute Monthly estimate using MultiAZ (using On-Demand pricing): $" +
                shapeCost.toFixed(2);
            }
            
           } else {
            if  ((temp_UseRI=='RI') && (rdsItemSelected.OracleCustom1yrNURI*1 > 0) ) {
              shapeCost = rdsItemSelected.OracleCustom1yrNURI * 730;
              RIused="RI"; 
              tempConfig.shape_summaryText =
                "RDS Compute Monthly SingleAZ estimate (using 1-year Reserved Instance pricing): $" +
                shapeCost.toFixed(2);
            } else {
              shapeCost = rdsItemSelected.OracleCustomOD * 730;
      
              tempConfig.shape_summaryText =
                "RDS Compute Monthly SingleAZ estimate (using On-Demand pricing): $" +
                shapeCost.toFixed(2);
            }
          }
          tempConfig.shape_cost = shapeCost;
       
     } else if (tempConfig.rds_type=="rac") {
      //Custom for RAC
      
          // tempConfig.UseMAZ="No";
          // config.UseMAZ="No";
    
          tempConfig.license_summaryText =
            "RDS Custom for Oracle RAC (BYOL)" +
            " for " +
            tempConfig.rac_nodes + " nodes of " +
            tempConfig.shape_optimize_vcpu +
            " VCPUs each ";
            tempConfig.license_summaryText +=
            "("+ 0.5 * tempConfig.shape_optimize_vcpu * tempConfig.rac_nodes +
            " Oracle Processor Licenses in total)";
          

    
          //todo: switch to RAC pricing when available
       
            //always use OD
            shapeCost = rdsItemSelected.OracleOD * 730 * tempConfig.rac_nodes;
      
            tempConfig.shape_summaryText =
                "RDS Compute Monthly estimate for " +  tempConfig.rac_nodes + " nodes (using On-Demand pricing): $" +
                shapeCost.toFixed(2);
            /*    
            if (rdsItemSelected.OracleCustom1yrNURI*1 > 0) {
              shapeCost = rdsItemSelected.OracleCustom1yrNURI * 730 * tempConfig.rac_nodes;
              RIused="RI"; 
              tempConfig.shape_summaryText =
                "RDS Compute Monthly estimate for " +  tempConfig.rac_nodes + " nodes (using 1-year Reserved Instance pricing): $" +
                shapeCost.toFixed(2);
            } else {
              // onDemand pricing for SE2 LI 
              shapeCost = rdsItemSelected.OracleCustomOD * 730 * tempConfig.rac_nodes;
      
              tempConfig.shape_summaryText =
                "RDS Compute Monthly estimate for " +  tempConfig.rac_nodes + " nodes (using On-Demand pricing): $" +
                shapeCost.toFixed(2);
            }
            */
            
          
          tempConfig.shape_cost = shapeCost;
       
  
  
     } else {
           //BYOL EE
    
          // is this bare metal?
          if (tempConfig.BareMetalProcessor) {
            // yes bare metal
              tempConfig.license_summaryText =
                "Oracle BYOL for db." +
                tempConfig.shape + " [ " +
                tempConfig.shape_max_vcpu/2 + " " +
                tempConfig.BareMetalProcessor+" Cores";
              if (tempConfig.UseMAZ=="Yes") {
                tempConfig.license_summaryText +=
                " per node. " + 1 * tempConfig.shape_max_vcpu  +
                " Cores total to account for both nodes of MultiAZ. ]";
              } else {
                tempConfig.license_summaryText += " ]";
              }  
          } else {
            // not bare metal
              tempConfig.license_summaryText =
                "Oracle BYOL for " +
                tempConfig.shape_optimize_vcpu +
                " VCPUs ";
              if (tempConfig.UseMAZ=="Yes") {
                tempConfig.license_summaryText +=
                "per node (" + 1 * tempConfig.shape_optimize_vcpu +
                " Oracle Processor Licenses total - accounts for licensing both nodes of MultiAZ)";
              } else {
                tempConfig.license_summaryText +=
                "("+ 0.5 * tempConfig.shape_optimize_vcpu +
                " Oracle Processor Licenses)";
              }  
        
              if (1 * tempConfig.shape_max_vcpu - tempConfig.shape_optimize_vcpu > 0) {
                tempConfig.license_summaryText =
                  tempConfig.license_summaryText +
                  ".  Optimize CPU feature used to go from " +
                  tempConfig.shape_max_vcpu +
                  " to " +
                  tempConfig.shape_optimize_vcpu +
                  " VCPUs.";
              }
        
              if ((config.optgoal_choice=="Y")&&(1 * tempConfig.shape_optimize_vcpu - tempConfig.requested_vcpu > 4)) {
                tempConfig.cpu_warnings_info =
                  tempConfig.cpu_warnings_info +
                  "[This shape does not appear to offer the Optimize CPU feature.] ";
              }
          }
    
          if (tempConfig.UseMAZ=="Yes") {
            
            if ((temp_UseRI=='RI') && tempConfig.shape_RI > 0) {
              shapeCost = rdsItemSelected.Oracle1yrNURI * 730 *2;
              RIused="RI"; 
              tempConfig.shape_summaryText =
                "RDS Compute Monthly estimate using MultiAZ (using 1-year Reserved Instance pricing): $" +
                shapeCost.toFixed(2);
            } else if ((temp_UseRI=='SP') && tempConfig.shape_SP > 0) {
              shapeCost = rdsItemSelected.Oracle1yrNUSP * 730 *2;
              RIused="SP"; 
              tempConfig.shape_summaryText =
                "RDS Compute Monthly estimate using MultiAZ (using 1-year Database Savings Plans pricing): $" +
                shapeCost.toFixed(2);
            } else {
              shapeCost = rdsItemSelected.OracleOD * 730 *2;
      
              tempConfig.shape_summaryText =
                "RDS Compute Monthly estimate using MultiAZ (using On-Demand pricing): $" +
                shapeCost.toFixed(2);
            }
            
           } else {
            if ((temp_UseRI=='RI') && tempConfig.shape_RI > 0) {
              shapeCost = rdsItemSelected.Oracle1yrNURI * 730;
              RIused="RI"; 
              tempConfig.shape_summaryText =
                "RDS Compute Monthly SingleAZ estimate (using 1-year Reserved Instance pricing): $" +
                shapeCost.toFixed(2);
            } else if ((temp_UseRI=='SP') && tempConfig.shape_SP > 0) {
              shapeCost = rdsItemSelected.Oracle1yrNUSP * 730;
              RIused="SP"; 
              tempConfig.shape_summaryText =
                "RDS Compute Monthly SingleAZ estimate (using 1-year Database Savings Plans pricing): $" +
                shapeCost.toFixed(2);
            } else {
              shapeCost = rdsItemSelected.OracleOD * 730;
      
              tempConfig.shape_summaryText =
                "RDS Compute Monthly SingleAZ estimate (using On-Demand pricing): $" +
                shapeCost.toFixed(2);
            }
          }
          tempConfig.shape_cost = shapeCost;
      
     } //end BYOL pricing
     
     tempConfig.RIused = RIused;
     
     if ( (tempConfig.UseMAZ=="Yes") && (tempConfig.rds_type!="rac")  ) {
        tempConfig.shape_max_vcpu_with_multiAZ= 2*tempConfig.shape_max_vcpu;
     }


      //
      //
      // STORAGE SECTION
      //
      //

      var rdsSnapshotSize =
        ((config.Backup * 60 * 60 * 24) / 1024) * config.SnapshotDays +
        working_DiskSpace * 0.7;
      // assume some factor (0.7?) for initial backup because RDS says most customers don't need backup space with RDS, so it must be less than 1.0.  Maybe accounts for allocated but unused blocks
      tempConfig.ebs_backup_snap_size = Math.max(0, 1 * rdsSnapshotSize - working_DiskSpace).toFixed(0);

     
      var tempCost=0.0;
      var RIstring = "";
      var mazString = " SingleAZ";
      var mazFactor = 1.0;
      if ( (tempConfig.UseMAZ=="Yes") && (tempConfig.rds_type!="rac")  ) {
        mazString=" using MultiAZ";
        mazFactor = 2.0;
      }
      if ((RIused=='RI') ) {
        RIstring = "using 1-year No Upfront Reserved Instance pricing";
      } else if ((RIused=='SP') ) {
        RIstring = "using 1-year No Upfront Database Savings Plans pricing";
      } else {
        RIstring = "using On-Demand pricing";
      }

      
      if ((config.VolumeType == "io1")  ) {
        

        //
        //
        // io1
        //
        //

        //estimate max io1 throughput based on iops
        let maxBW = 0;
        if (working_IOPS*1<8000) {
          maxBW = 2000 * working_IOPS / 8000;
        }
        if (working_IOPS*1>=8000 && working_IOPS*1<128000) {
          maxBW=2000;
        }
        if (working_IOPS*1>=128000) {
          maxBW=4000 * Math.min(working_IOPS,256000) / 256000;
        }
        // console.log("maxBW",maxBW, working_Throughput);
        if (maxBW<working_Throughput*1) {
          // console.log("maxBW IF HERE");
          let neededIOPS=0;
          if (working_Throughput*1 < 2000) {
            neededIOPS = working_Throughput * 8000 / 2000 + 1;
          }
          if (working_Throughput*1 >= 2000) {
            neededIOPS = working_Throughput * 256000 / 4000 + 1;
          }
          tempConfig.iops_warnings_info =
            tempConfig.iops_warnings_info + "[io1 with "+working_IOPS+" IOPS can only do a max Throughput of "+maxBW.toFixed(0)+" MB/s. For pricing, we increased the IOPS to "+ neededIOPS.toFixed(0) + " to be able to hit "+working_Throughput+ " MB/s.] ";
          
            
          tempConfig.iomb_warnings_info =
            tempConfig.iomb_warnings_info + "[io1 with "+working_IOPS+" IOPS can only do a max Throughput of "+maxBW.toFixed(0)+" MB/s. For pricing, we increased the IOPS to "+ neededIOPS.toFixed(0) + " to be able to hit "+working_Throughput+ " MB/s.] ";
            
          working_IOPS=Math.round(neededIOPS);
        }

        tempConfig.ebs_burst_Throughput = Math.round(Math.max(maxBW, Math.min(4000,working_Throughput) ) );
        
        //now do some warnings for io1
        if (1 * tempConfig.requested_throughput > 4000) {
          //single io1 volume can do 1000MB/s.  RDS-Oracle appears to use 4 EBS volumes if disk size is ~ >300GB.
          tempConfig.iomb_warnings_alert =
            tempConfig.iomb_warnings_alert + "[io1 for RDS-Oracle will not be able to exceed 4000 MB/s.] ";
        }

        if (1 * working_IOPS < 1000) {
          tempConfig.iops_warnings_info =
            tempConfig.iops_warnings_info + "[io1 IOPS must be at least 1000.  Using 1000 IOPS for pricing.] ";
            working_IOPS=1000;
        }
        if (1 * working_IOPS > 256000)  {
          tempConfig.iops_warnings_alert =
            tempConfig.iops_warnings_alert + "[io1 IOPS must be less than 256000.] ";
        }
        /* if ((1 * working_IOPS > 40000) && !(
           (tempConfig.shape=='db.m5.24xlarge')||
           (tempConfig.shape=='db.r5.24xlarge')||
           (tempConfig.shape=='db.x1.32xlarge')||
           (tempConfig.shape=='db.x1e.32xlarge')
        )) {
          tempConfig.iomb_warnings =
            tempConfig.iomb_warnings + "io1 IOPS must be less than 40000 with this shape. ";
        } */
        if (1 * working_IOPS < 0.5*working_DiskSpace) {
          working_IOPS=Math.round(0.5*working_DiskSpace);
          tempConfig.iops_warnings_info =
            tempConfig.iops_warnings_info + "[io1 IOPS must be at least 1/2 the Disk Space in GB.  Setting IOPS to "+working_IOPS+ " for pricing.] ";
 
        }
        // if (1 * working_IOPS < 1*working_DiskSpace) {
        //   tempConfig.IOPS_warnings =
        //     tempConfig.IOPS_warnings + "io1 IOPS are suggested to be at least same as the Disk Space in GB. ";
        // }

         if (1 * working_DiskSpace < 100) {
          tempConfig.iops_warnings_info =
            tempConfig.iops_warnings_info + "[io1 Disk Size must be at least 100 GB.] ";
        }
        if (1 * working_DiskSpace > 64000) {
          tempConfig.iops_warnings_alert =
            tempConfig.iops_warnings_alert + "[io1 Disk Size must be less than 64 TB.] ";
        }
         
        tempConfig.ebs_data_summaryText1 =
          "" +
          working_DiskSpace +
          " GB of io1 (Provisioned IOPS SSD) at " +
          working_IOPS +
          " IOPS.";
        tempConfig.ebs_data_summaryText2 =
          "RDS Storage"+mazString+" Monthly estimate: EBS Volumes=$" +
          (working_DiskSpace * regionInfo.rds_io1_gb * mazFactor).toFixed(2) +
          ", EBS IOPS=$" +
          (regionInfo.rds_io1_iops * working_IOPS * mazFactor).toFixed(2);

        tempConfig.ebs_data_iops = working_IOPS;

        

        tempConfig.ebs_burst_IOPS = working_IOPS;
        
        
        tempConfig.ebs_data_cost = working_DiskSpace * regionInfo.rds_io1_gb * mazFactor +
            regionInfo.rds_io1_iops * working_IOPS * mazFactor ;
        tempConfig.ebs_backup_cost =
            Math.max(0, regionInfo.rds_backup_gb * (1 * rdsSnapshotSize - working_DiskSpace));

        tempCost=(
            shapeCost +
            working_DiskSpace * regionInfo.rds_io1_gb * mazFactor +
            regionInfo.rds_io1_iops * working_IOPS * mazFactor +
            Math.max(0, regionInfo.rds_backup_gb * (1 * rdsSnapshotSize - working_DiskSpace))
          );

        tempConfig.summaryText += " Monthly" + mazString+" = [" +
          rdsItemSelected.Name;
        if (tempConfig.rds_type=="rac") {
          tempConfig.summaryText += " x "+tempConfig.rac_nodes;
        }  
        
        tempConfig.summaryText += " " + RIstring +
          "] + [io1 @ " +
          working_DiskSpace +
          " GB at " +
          working_IOPS +
          " IOPS]" +
          // " + [additional " +
          // Math.max(0, 1 * rdsSnapshotSize - working_DiskSpace).toFixed(0) +
          // " GB of Snapshots]" +
          " = $" +
          tempCost.toFixed(2);
          
       

      } else if ((tempConfig.rds_type=="rac") && ((config.VolumeType == "io2")||(config.VolumeType == "io2bx") ) ) {        

        //
        //
        // Custom for RAC and io2 and io2bx
        //
        //
        
        
        var iopsCost=0.0;
        var volumes = 16; //number of EBS volumes
        var redundancy = 2; //ASM redunancy
        if ( (config.VolumeType == "io2") && (working_DiskSpace*1<=128000) ) {
          volumes = 8;
        }
        // if ( (config.VolumeType == "io2") && (working_DiskSpace*1<=32000) ) {
        //   volumes = 8;
        // }
        var vol_IOPS = working_IOPS / volumes; //should we also divide by redundancy??

        //estimate max io2 throughput based on iops
        let maxBW = 0;
         
        if (config.VolumeType=="io2old") {
          
          if (vol_IOPS*1<2000) {
            maxBW = 500 * vol_IOPS / 2000;
          }
          if (vol_IOPS*1>=2000 && vol_IOPS*1<32000) {
            maxBW=500;
          }
          if (vol_IOPS*1>=32000) {
            maxBW=1000 * Math.min(64000,vol_IOPS) / 64000;
          }
          // console.log("maxBW",maxBW, working_Throughput);
          if (maxBW*volumes<working_Throughput*1) {
            // console.log("maxBW IF HERE");
            //don't print this message if we are already beyond the Throughput capacity of the normal volumes
            if (1 * tempConfig.requested_throughput <= 1000*volumes) {
              let neededIOPS=0;
              if (working_Throughput*1/volumes < 500) {
                // neededIOPS = working_Throughput * 2000 / (500) + 1;
                neededIOPS = working_Throughput / 0.256 + 1;
              }
              if (working_Throughput*1/volumes >= 500) {
                neededIOPS = working_Throughput * 64000 / (1000) + 1;
              }
              tempConfig.iops_warnings_info =
                tempConfig.iops_warnings_info + "[io2 with "+vol_IOPS*volumes+" IOPS can only do a max Throughput of "+(maxBW*volumes).toFixed(0)+" MB/s. For pricing, we increased the IOPS to "+ neededIOPS.toFixed(0) + " to be able to hit "+working_Throughput+ " MB/s.] ";
              working_IOPS=Math.round(neededIOPS);
              tempConfig.iomb_warnings_info =
                tempConfig.iomb_warnings_info + "[io2 with "+vol_IOPS*volumes+" IOPS can only do a max Throughput of "+(maxBW*volumes).toFixed(0)+" MB/s. For pricing, we increased the IOPS to "+ neededIOPS.toFixed(0) + " to be able to hit "+working_Throughput+ " MB/s.] ";
            }
          }
          
          tempConfig.ebs_burst_Throughput = Math.round(Math.max(maxBW*volumes, Math.min(1000*volumes, working_Throughput) ) );
            
        } else {
          //do similar calc for io2bx
           maxBW = 0;
          if (vol_IOPS*1<16000) {
            maxBW = 4000 * vol_IOPS / 16000;
          }
          if (vol_IOPS*1>=16000) {
            maxBW=4000;
          }
         
          // console.log("maxBW",maxBW, working_Throughput);
          if (maxBW*volumes<working_Throughput*1) {
            // console.log("maxBW IF HERE");
            //don't print this message if we are already beyond the Throughput capacity of the normal volumes
            if (1 * tempConfig.requested_throughput <= 4000*volumes) {
              let neededIOPS=0;
              if (working_Throughput*1/volumes < 4000) {
                // neededIOPS = working_Throughput * 16000 / 4000 + 1;
                neededIOPS = working_Throughput / 0.256 + 1;
              }
              // if (working_Throughput*1/volumes >= 4000) {
              //   neededIOPS = working_Throughput * 256000 / 4000 + 1;
              // }
              tempConfig.iops_warnings_info =
                tempConfig.iops_warnings_info + "[io2bx with "+vol_IOPS*volumes+" IOPS can only do a max Throughput of "+(maxBW*volumes).toFixed(0)+" MB/s. For pricing, we increased the IOPS to "+ neededIOPS.toFixed(0) + " to be able to hit "+working_Throughput+ " MB/s.] ";
              working_IOPS=Math.round(neededIOPS);
              tempConfig.iomb_warnings_info =
                tempConfig.iomb_warnings_info + "[io2bx with "+vol_IOPS*volumes+" IOPS can only do a max Throughput of "+(maxBW*volumes).toFixed(0)+" MB/s. For pricing, we increased the IOPS to "+ neededIOPS.toFixed(0) + " to be able to hit "+working_Throughput+ " MB/s.] ";
            }  
          }
          
          tempConfig.ebs_burst_Throughput = Math.round(Math.max(maxBW*volumes, Math.min(4000*volumes, working_Throughput) ) );
            
          
        }  
        
        if (1 * working_IOPS < 2400) {
          working_IOPS=2400;
          tempConfig.iops_warnings_info =
            tempConfig.iops_warnings_info + "[io2 IOPS must be at least 2400.  Setting IOPS to "+working_IOPS+ " for pricing.] ";
        }
        
        // console.log("maxBW", maxBW);

        
        vol_IOPS = working_IOPS / volumes; //recalculate
                
        //now do some warnings for io2
        if ( (config.VolumeType == "io2old") && (1 * tempConfig.requested_throughput > 1000*volumes) ) {
          //single io2 volume can do 1000MB/s.  
          tempConfig.iomb_warnings_alert =
            tempConfig.iomb_warnings_alert + "[io2 for RDS Custom for RAC will not be able to exceed "+1000*volumes+" MB/s with the standard "+volumes+ " data volumes for this size database.  You should add "+ Math.ceil( (tempConfig.requested_throughput - 1000*volumes) / 1000 ) +" additional data volumes (not included in this price estimate) to ASM to meet "+tempConfig.requested_throughput+" MB/s.] ";
        }
        if ( (config.VolumeType == "io2") && (1 * tempConfig.requested_throughput > 4000*volumes) ) {
          //single io2bx volume can do 4000MB/s.  
          tempConfig.iomb_warnings_alert =
            tempConfig.iomb_warnings_alert + "[io2bx for RDS Custom for RAC will not be able to exceed "+4000*volumes+" MB/s with the standard "+volumes+ " data volumes for this size database.  You should add "+ Math.ceil( (tempConfig.requested_throughput - 4000*volumes) / 4000 ) +" additional data volumes (not included in this price estimate) to ASM to meet "+tempConfig.requested_throughput+" MB/s.] ";
        }

        if ( (config.VolumeType == "io2") && (1 * working_IOPS > 256000*volumes) ) {
          tempConfig.iops_warnings_alert =
            tempConfig.iops_warnings_alert + "[io2bx IOPS must be less than "+256000*volumes+".] ";
        }
        if ( (config.VolumeType == "io2old") && (1 * working_IOPS > 64000*volumes) ) {
          tempConfig.iops_warnings_alert =
            tempConfig.iops_warnings_alert + "[io2 IOPS must be less than "+64000*volumes+".] ";
        }
        // if (1 * vol_IOPS < 0.5*working_DiskSpace) {
        //   tempConfig.IOPS_warnings =
        //     tempConfig.IOPS_warnings + "[io2 IOPS must be at least 1/2 the Disk Space in GB.] ";
        // }
        // if (1 * vol_IOPS < 1*working_DiskSpace) {
        //   tempConfig.IOPS_warnings =
        //     tempConfig.IOPS_warnings + "io1 IOPS are suggested to be at least same as the Disk Space in GB. ";
        // }

         if (1 * working_DiskSpace < 256) {
           working_DiskSpace=256;
          tempConfig.ebsvol_warnings =
            tempConfig.ebsvol_warnings + "[using io2 minimum disk space of 256GB for pricing.] ";
        }
        


        config.addlVolumes = " (broken out as "+volumes+" volumes of "+(working_DiskSpace*redundancy/volumes).toFixed(0)+"GB at "+vol_IOPS.toFixed(0)+" IOPS. Assumes normal ASM redundancy.)";
        
        if (vol_IOPS<=32000) {
          iopsCost=vol_IOPS*regionInfo.ec2_io2_iops1;
        } else {
          iopsCost=32000*regionInfo.ec2_io2_iops1;
          if (vol_IOPS<=64000) {
            iopsCost=32000*regionInfo.ec2_io2_iops1 + (vol_IOPS-32000)*regionInfo.ec2_io2_iops2;
          } else {
            iopsCost=32000*regionInfo.ec2_io2_iops1 + 32000*regionInfo.ec2_io2_iops2 + (vol_IOPS-64000)*regionInfo.ec2_io2_iops3;
            if ( !(regionInfo.ec2_io2_iops3*1>0) ) {
              //if this is not >0 then it means we are in a region with io2bx but trying to use it
              tempConfig.iops_warnings_alert =
                 tempConfig.iops_warnings_alert + "[ERROR: It looks like we are trying to provision more than 64000 IOPS per volume in a region that does not support io2bx.  Desired IOPS per volume="+vol_IOPS+"] ";

            }
          }
        }
        iopsCost = iopsCost*volumes; //add up IOPS costs for all volumes

        tempConfig.ebs_data_iops = working_IOPS;
        tempConfig.ebs_data_cost = (working_DiskSpace * redundancy * regionInfo.ec2_io2_gb) +iopsCost;

 
        tempConfig.ebs_data_summaryText1 =
          "" +
          working_DiskSpace +
          " GB of "+config.VolumeType+" (Provisioned IOPS SSD) at " +
          working_IOPS +
          " IOPS.";
        

        tempConfig.ebs_data_summaryText2 =
          "RDS Storage Monthly estimate: EBS Volumes=$" +
          (tempConfig.ebs_data_cost).toFixed(2)  +
          " [Storage=$"+
          (working_DiskSpace * redundancy * regionInfo.ec2_io2_gb).toFixed(2) +
          ", IOPS=$" +
          (iopsCost).toFixed(2) + "] " ;

        // tempConfig.ebs_data_iops = vol_IOPS;

        

        tempConfig.ebs_burst_IOPS = working_IOPS;
        
        
        tempConfig.ebs_backup_cost =
            Math.max(0, regionInfo.rds_backup_gb * (1 * rdsSnapshotSize - working_DiskSpace));

        tempCost=(
            shapeCost +
            tempConfig.ebs_data_cost +
            tempConfig.ebs_backup_cost
          );

        tempConfig.summaryText += " Monthly = [" +
          rdsItemSelected.Name;
        if (tempConfig.rds_type=="rac") {
          tempConfig.summaryText += " x "+tempConfig.rac_nodes;
        }  
        tempConfig.summaryText += " " + RIstring +
          "] + ["+config.VolumeType+" @ " +
          working_DiskSpace +
          " GB at " +
          working_IOPS +
          " IOPS]" +
          // " + [additional " +
          // Math.max(0, 1 * rdsSnapshotSize - working_DiskSpace).toFixed(0) +
          // " GB of Snapshots]" +
          " = $" +
          tempCost.toFixed(2);
          



          
        

      } else if ((tempConfig.rds_type!="rac") && ((config.VolumeType == "io2") ) ) {        

        //
        //
        // non-RAC with io2 
        //
        //
        
        // Volume throughput up to 4,000 MiB/s. Throughput scales proportionally at a rate of 0.256 MiB/s per provisioned IOPS. Maximum throughput can be achieved at 16,000 IOPS or higher.
        // https://docs.aws.amazon.com/ebs/latest/userguide/provisioned-iops.html
        
        
        var iopsCost=0.0; // we populate this later
        var volumes = 4 * (1+tempConfig.AddlStorageVolumes); //number of EBS volumes
        var vol_IOPS = working_IOPS / volumes; 

        //estimate max io2 throughput based on iops
        let maxBW = 0;
         
        if (config.VolumeType=="io2") {
          
           maxBW = 0;
          if (vol_IOPS*1<16000) {
            maxBW = 4000 * vol_IOPS / 16000;
          }
          if (vol_IOPS*1>=16000) {
            maxBW=4000;
          }
         
          // console.log("maxBW",maxBW, working_Throughput);
          if (maxBW*volumes<working_Throughput*1) {
            // console.log("maxBW IF HERE");
            //don't print this message if we are already beyond the Throughput capacity of the normal volumes
            if (1 * tempConfig.requested_throughput <= 4000*volumes) {
              let neededIOPS=0;
              if (working_Throughput*1/volumes < 4000) {
                // neededIOPS = working_Throughput * 16000 / 4000 + 1;
                neededIOPS = working_Throughput / 0.256 + 1;
              }
              // if (working_Throughput*1/volumes >= 4000) {
              //   neededIOPS = working_Throughput * 256000 / 4000 + 1;
              // }
              tempConfig.iops_warnings_info =
                tempConfig.iops_warnings_info + "[io2 with "+vol_IOPS*volumes+" IOPS can only do a max Throughput of "+(maxBW*volumes).toFixed(0)+" MB/s. For pricing, we increased the IOPS to "+ neededIOPS.toFixed(0) + " to be able to hit "+working_Throughput+ " MB/s.] ";
              working_IOPS=Math.round(neededIOPS);
              tempConfig.iomb_warnings_info =
                tempConfig.iomb_warnings_info + "[io2 with "+vol_IOPS*volumes+" IOPS can only do a max Throughput of "+(maxBW*volumes).toFixed(0)+" MB/s. For pricing, we increased the IOPS to "+ neededIOPS.toFixed(0) + " to be able to hit "+working_Throughput+ " MB/s.] ";
            }  
          }
          
          tempConfig.ebs_burst_Throughput = Math.round(Math.max(maxBW*volumes, Math.min(4000*volumes, working_Throughput) ) );
            
          
        }  
        
        

        
        vol_IOPS = working_IOPS / volumes; //recalculate
                
        //now do some warnings for io2
        if ( (config.VolumeType == "io2") && (1 * tempConfig.requested_throughput > 4000*volumes) ) {
          //single io2bx volume can do 4000MB/s.  
          tempConfig.iomb_warnings_alert =
            tempConfig.iomb_warnings_alert + "[io2 will not be able to exceed "+4000*volumes+" MB/s with "+volumes+ " EBS volumes. ] ";
        }


        if ( (config.VolumeType == "io2") && (1 * working_IOPS > 64000*volumes) ) {
          tempConfig.iops_warnings_alert =
            tempConfig.iops_warnings_alert + "[io2 IOPS must be less than "+64000*volumes+".] ";
        }
        // if (1 * vol_IOPS < 0.5*working_DiskSpace) {
        //   tempConfig.IOPS_warnings =
        //     tempConfig.IOPS_warnings + "[io2 IOPS must be at least 1/2 the Disk Space in GB.] ";
        // }
        // if (1 * vol_IOPS < 1*working_DiskSpace) {
        //   tempConfig.IOPS_warnings =
        //     tempConfig.IOPS_warnings + "io1 IOPS are suggested to be at least same as the Disk Space in GB. ";
        // }

         if (1 * working_DiskSpace < 100) {
           working_DiskSpace=100;
          tempConfig.ebsvol_warnings =
            tempConfig.ebsvol_warnings + "[using io2 minimum disk space of 100GB for pricing.] ";
        }
        



        iopsCost=vol_IOPS*regionInfo.rds_io2_iops;
        

        iopsCost = iopsCost*volumes * mazFactor; //add up IOPS costs for all volumes

        tempConfig.ebs_data_iops = working_IOPS;
        tempConfig.ebs_data_cost = (working_DiskSpace * regionInfo.rds_io2_gb * mazFactor) +iopsCost;

 
        tempConfig.ebs_data_summaryText1 =
          "RDS Storage"+mazString+": " +
          working_DiskSpace +
          " GB of "+config.VolumeType+" (Provisioned IOPS SSD) at " +
          working_IOPS +
          " IOPS" +
          ((tempConfig.AddlStorageVolumes*1>0) ? " using "+ tempConfig.AddlStorageVolumes+ " Additional io2 Storage Volumes." : ".");
        

        tempConfig.ebs_data_summaryText2 =
          "RDS Storage"+mazString+" Monthly estimate: EBS Volumes=$" +
          (tempConfig.ebs_data_cost).toFixed(2)  +
          " [Storage=$"+
          (working_DiskSpace * regionInfo.rds_io2_gb * mazFactor).toFixed(2) +
          ", IOPS=$" +
          (iopsCost).toFixed(2) + "] " + 
          ((tempConfig.AddlStorageVolumes*1>0) ? " using "+ tempConfig.AddlStorageVolumes+ " Additional io2 Storage Volumes. " : "");

        // tempConfig.ebs_data_iops = vol_IOPS;

        

        tempConfig.ebs_burst_IOPS = working_IOPS;
        
        
        tempConfig.ebs_backup_cost =
            Math.max(0, regionInfo.rds_backup_gb * (1 * rdsSnapshotSize - working_DiskSpace));

        tempCost=(
            shapeCost +
            tempConfig.ebs_data_cost +
            tempConfig.ebs_backup_cost
          );

        tempConfig.summaryText += " Monthly" + mazString + " = [" +
          rdsItemSelected.Name;
        if (tempConfig.rds_type=="rac") {
          tempConfig.summaryText += " x "+tempConfig.rac_nodes;
        }  
        tempConfig.summaryText += " " + RIstring +
          "] + ["+config.VolumeType+" @ " +
          working_DiskSpace +
          " GB at " +
          working_IOPS +
          " IOPS" +
          ((tempConfig.AddlStorageVolumes*1>0) ? " using "+ tempConfig.AddlStorageVolumes+ " Additional io2 Storage Volumes]" : "]") +
          " = $" +
          tempCost.toFixed(2);



          
          
      } else if (config.VolumeType == "gp3") {

        //
        //
        // gp3
        //
        //
        
        var baseIops=3000;
        var baseThru=125;
        if (working_DiskSpace*1>=200) {
          baseIops=12000;
          baseThru=500;
        } 
        
        // if using ASV, our base values are even bigger
        if (tempConfig.AddlStorageVolumes*1>0) {
          baseIops = baseIops * (1+tempConfig.AddlStorageVolumes);
          baseThru = baseThru * (1+tempConfig.AddlStorageVolumes);
        }

        if (1 * working_IOPS > 500*working_DiskSpace) {
          tempConfig.iops_warnings_alert =
            tempConfig.iops_warnings_alert + "[gp3 IOPS must be less than 500x the Disk Space in GB.] ";
        }

        if (1 * working_Throughput > 0.25*Math.max(working_IOPS,baseIops)) {

              tempConfig.iops_warnings_info =
                tempConfig.iops_warnings_info + "[gp3 Throughput in MB/s must be less than 0.25x the IOPS. For pricing, we increased the IOPS to "+ working_Throughput*4 + " to be able to hit "+working_Throughput+ " MB/s.] ";
              
              tempConfig.iomb_warnings_info =
                tempConfig.iomb_warnings_info + "[gp3 Throughput in MB/s must be less than 0.25x the IOPS. For pricing, we increased the IOPS to "+ working_Throughput*4 + " to be able to hit "+working_Throughput+ " MB/s.] ";
              working_IOPS=working_Throughput*4;

        }
        



        //calculate additional IOPS and throughput charges. 
        var iopsCost=0.0;
        var mbsCost=0.0;
        if (working_IOPS*1>baseIops) {
          iopsCost=(working_IOPS-baseIops)*regionInfo.rds_gp3_iops* mazFactor;
        } 
        if (tempConfig.requested_throughput*1>baseThru) {
          mbsCost=(tempConfig.requested_throughput-baseThru)*regionInfo.rds_gp3_thru * mazFactor; 
        } 
        
        

        tempConfig.ebs_data_summaryText1 =
          "" +
          working_DiskSpace +
          " GB of gp3 (General Purpose SSD) at " +
          Math.max(tempConfig.requested_throughput,baseThru) +
          " MB/s and " +
          Math.max(working_IOPS,baseIops) +
          " IOPS" +
          ((tempConfig.AddlStorageVolumes*1>0) ? " using "+ tempConfig.AddlStorageVolumes+ " Additional gp3 Storage Volumes." : ".");  
          
       
        tempConfig.ebs_data_iops = Math.max(working_IOPS,baseIops);
        tempConfig.ebs_burst_IOPS = tempConfig.ebs_data_iops;        
        
        tempConfig.ebs_burst_Throughput = Math.max(tempConfig.requested_throughput,baseThru);
        
        tempConfig.ebs_data_cost = (working_DiskSpace * regionInfo.rds_gp3_gb* mazFactor) +iopsCost + mbsCost;
        
        tempConfig.ebs_data_iops_cost = iopsCost;
        tempConfig.ebs_data_throughput_cost = mbsCost;

        tempConfig.ebs_data_summaryText2 =
          "RDS Storage"+mazString+" Monthly estimate: EBS Volumes=$" +
          (tempConfig.ebs_data_cost).toFixed(2) + " [Storage=$"+
          (working_DiskSpace * regionInfo.rds_gp3_gb* mazFactor).toFixed(2) +
          ", additional IOPS=$" +
          (iopsCost).toFixed(2) +
        ", additional MB/s=$" +
          (mbsCost).toFixed(2) +"] " + 
          ((tempConfig.AddlStorageVolumes*1>0) ? " using "+ tempConfig.AddlStorageVolumes+ " Additional gp3 Storage Volumes. " : "");

        tempConfig.ebs_backup_cost =
            Math.max(0, regionInfo.rds_backup_gb * (1 * rdsSnapshotSize - working_DiskSpace));

        tempCost=(
            shapeCost +
            tempConfig.ebs_data_cost + 
            tempConfig.ebs_backup_cost
          );

        tempConfig.summaryText += " Monthly" + mazString+" = [" +
          rdsItemSelected.Name;
        if (tempConfig.rds_type=="rac") {
          tempConfig.summaryText += " x "+tempConfig.rac_nodes;
        }  
        tempConfig.summaryText += " " + RIstring +
          "] + [gp3 @ " +
          working_DiskSpace +
          " GB at " +
          Math.max(tempConfig.requested_throughput,baseThru) +
          " MB/s and " +
          Math.max(working_IOPS,baseIops) +
          " IOPS" +
          ((tempConfig.AddlStorageVolumes*1>0) ? " using "+ tempConfig.AddlStorageVolumes+ " Additional gp3 Storage Volumes]" : "]") +
          " = $" +
          tempCost.toFixed(2);

        // warnings for gp3
       if (1 * tempConfig.requested_throughput > 4000*(1+tempConfig.AddlStorageVolumes)) {
          //single gp volume can do 1000MB/s.  RDS-Oracle appears to use 4 EBS volumes if disk size is ~ >200GB.
          tempConfig.iomb_warnings_alert =
            tempConfig.iomb_warnings_alert + "[gp3 for RDS-Oracle will not be able to exceed 4000 MB/s per logical volume.] ";
        }

        if (1 * working_IOPS > 64000*(1+tempConfig.AddlStorageVolumes))  {
          tempConfig.iops_warnings_alert =
            tempConfig.iops_warnings_alert + "[gp3 IOPS must be less than 64000 per logical volume.] ";
        }
        

         if (1 * working_DiskSpace < 20) {
          tempConfig.iops_warnings_info =
            tempConfig.iops_warnings_info + "[gp3 Disk Size must be at least 20 GB.] ";
        }
        if (1 * working_DiskSpace > 64000*(1+tempConfig.AddlStorageVolumes)) {
          tempConfig.iops_warnings_alert =
            tempConfig.iops_warnings_alert + "[gp3 Disk Size must be less than 64 TB per logical volume.] ";
        }
        
 
         
          
      } else {  

        //
        //
        // gp2
        //
        //

        tempConfig.ebs_data_summaryText1 =
          "" +
          working_DiskSpace +
          " GB of gp2 (General Purpose SSD)";
        tempConfig.ebs_data_summaryText2 =
          "RDS Storage"+mazString+" Monthly estimate: EBS Volumes=$" +
          (working_DiskSpace * regionInfo.rds_gp2_gb * mazFactor).toFixed(2);

        // https://aws.amazon.com/blogs/database/understanding-burst-vs-baseline-performance-with-amazon-rds-and-gp2/
    
        /* if (1 * working_IOPS > 64000) {
          tempConfig.iomb_warnings =
            tempConfig.iomb_warnings + "gp2 IOPS must be less than 64000. ";
        } */
        if (1 * working_DiskSpace >= 200) {
          // if (1 * tempConfig.requested_throughput > 1000) {
          //   //single gp2 volume can do 250MB/s.  RDS-Oracle appears to use 4 EBS volumes if disk size is ~ >200GB.
          //   tempConfig.iomb_warnings =
          //     tempConfig.iomb_warnings + "[gp2 for RDS-Oracle will not be able to exceed 1000 MB/s.  You should use gp3/io1 for this environment.] ";
          // }
          tempConfig.ebs_baseline_IOPS = Math.min(working_DiskSpace/4 * 3, 16000)*4;

          if (working_DiskSpace*1<170*4) {
            // tempConfig.ebs_baseline_Throughput=128*4;
            tempConfig.ebs_burst_Throughput=128*4;
          } else if (working_DiskSpace*1>=334*4) {
            // tempConfig.ebs_baseline_Throughput=250*4;
            tempConfig.ebs_burst_Throughput=250*4;
          } else {
            tempConfig.ebs_baseline_Throughput=128*4;
            tempConfig.ebs_burst_Throughput=250*4;
          } 
          if (1 * tempConfig.requested_throughput > tempConfig.ebs_burst_Throughput) {
            //single gp2 volume can do 250MB/s.  RDS-Oracle appears to use 4 EBS volumes if disk size is ~ >200GB.
            tempConfig.iomb_warnings_alert =
              tempConfig.iomb_warnings_alert + "["+working_DiskSpace+"GB of gp2 for RDS-Oracle will not be able to exceed "+tempConfig.ebs_burst_Throughput+" MB/s.  You should use gp3/io2 for this environment.] ";
          }
 
          if (1 * working_IOPS > tempConfig.ebs_baseline_IOPS +2 ) {
            tempConfig.iops_warnings_burst =
              tempConfig.iops_warnings_burst +
              "[Calculated gp2 baseline IOPS of " +
              tempConfig.ebs_baseline_IOPS +
              " is less than your desired " +
              working_IOPS +
              " IOPS (please confirm that the gp2 bursting profile fits your workload).] ";
          }
          //burst: https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/CHAP_Storage.html
          //minimum of 100 iops per volume
          //no more than 3000 per volume (unless baseline is higher)
          
          tempConfig.ebs_burst_IOPS = Math.max(3000*4,tempConfig.ebs_baseline_IOPS);
          
          if (1 * working_IOPS > tempConfig.ebs_burst_IOPS +2) {
            tempConfig.iops_warnings_alert =
              tempConfig.iops_warnings_alert +
              "[Calculated gp2 bursting IOPS of " +
              tempConfig.ebs_burst_IOPS +
              " is less than your desired " +
              working_IOPS +
              " IOPS (please switch to gp3 or io2).] ";
          }
          
          
          
        } else {
          //assume 1 volume and less than 200GB
          // if (1 * tempConfig.requested_throughput > 250) {
          //   //single gp2 volume can do 250MB/s.  RDS-Oracle appears to use 4 EBS volumes if disk size is ~ >400GB.
          //   tempConfig.iomb_warnings =
          //     tempConfig.iomb_warnings + "[gp2 for RDS-Oracle will not be able to exceed 250 MB/s when storage size is <200GB.  You should use gp3/io1 for this environment.] ";
          // }
          tempConfig.ebs_baseline_IOPS = Math.max(100,Math.min(working_DiskSpace * 3, 16000));
         
          
          if (working_DiskSpace*1<170) {
            // tempConfig.ebs_baseline_Throughput=128;
            tempConfig.ebs_burst_Throughput=128;
          } else {
            tempConfig.ebs_baseline_Throughput=128;
            tempConfig.ebs_burst_Throughput=250;
           
          }  
          if (1 * tempConfig.requested_throughput > tempConfig.ebs_burst_Throughput) {
            //single gp2 volume can do 250MB/s.  RDS-Oracle appears to use 4 EBS volumes if disk size is ~ >200GB.
            tempConfig.iomb_warnings_alert =
              tempConfig.iomb_warnings_alert + "["+working_DiskSpace+"GB of gp2 for RDS-Oracle will not be able to exceed "+tempConfig.ebs_burst_Throughput+" MB/s.  You should use gp3/io2 for this environment.] ";
          }
          
          if (1 * working_IOPS > tempConfig.ebs_baseline_IOPS + 2) {
            tempConfig.iops_warnings_burst =
              tempConfig.iops_warnings_burst +
              "[Calculated gp2 baseline IOPS of " +
              tempConfig.ebs_baseline_IOPS +
              " is less than your desired " +
              working_IOPS +
              " IOPS (please confirm that the gp2 bursting profile fits your workload).] ";
          }

          tempConfig.ebs_burst_IOPS = Math.max(3000,tempConfig.ebs_baseline_IOPS);
          
          if (1 * working_IOPS > tempConfig.ebs_burst_IOPS +2) {
            tempConfig.iops_warnings_alert =
              tempConfig.iops_warnings_alert +
              "[Calculated gp2 bursting IOPS of " +
              tempConfig.ebs_burst_IOPS +
              " is less than your desired " +
              working_IOPS +
              " IOPS (please switch to gp3 or io2).] ";
          }
          
        }
        if (1 * working_DiskSpace < 20) {
          tempConfig.iops_warnings_info =
            tempConfig.iops_warnings_info + "[gp2 Disk Size must be at least 20 GB.] ";
        }
        if (1 * working_DiskSpace > 64000) {
          tempConfig.iops_warnings_alert =
            tempConfig.iops_warnings_alert + "[gp2 Disk Size must be less than 64 TB.] ";
        }

        tempCost=(
            shapeCost +
            working_DiskSpace * regionInfo.rds_gp2_gb * mazFactor +
            Math.max(0, regionInfo.rds_backup_gb * (1 * rdsSnapshotSize - working_DiskSpace))
          );
        tempConfig.ebs_backup_cost =  Math.max(0, regionInfo.rds_backup_gb * (1 * rdsSnapshotSize - working_DiskSpace));
        tempConfig.ebs_data_cost = working_DiskSpace * regionInfo.rds_gp2_gb * mazFactor;

        tempConfig.summaryText += " Monthly" +mazString+" = [" +
          rdsItemSelected.Name;
        if (tempConfig.rds_type=="rac") {
          tempConfig.summaryText += " x "+tempConfig.rac_nodes;
        }  
        tempConfig.summaryText += " " + RIstring +
          "] + [gp2 @ " +
          working_DiskSpace +
          " GB]" +
          // " + [additional " +
          // Math.max(0, 1 * rdsSnapshotSize - working_DiskSpace).toFixed(0) +
          // " GB of Snapshots]"+
          " = $" +
          tempCost.toFixed(2);
          

      }


      // Add a message that we are using ASVs
      if (config.targetconfig_rds.AddlStorageVolumes*1>0) {
        config.targetconfig_rds.ebsvol_warnings = config.targetconfig_rds.ebsvol_warnings + "[Adding "+config.targetconfig_rds.AddlStorageVolumes+ " Additional Storage Volumes to meet requirements] ";
        config.addlVolumes = "For pricing, assume storage, IOPS, and Throughput are spread across the default volume and the "+ config.targetconfig_rds.AddlStorageVolumes + 
          " Additional Storage Volumes evenly.  Each contains " +
          (working_DiskSpace/(config.targetconfig_rds.AddlStorageVolumes+1)).toFixed(0)+" GB";

          // " supporting " + 
          // (working_IOPS/(config.targetconfig_rds.AddlStorageVolumes+1)).toFixed(0)+" IOPS and " +
          // (working_Throughput/(config.targetconfig_rds.AddlStorageVolumes+1)).toFixed(0)+" MB/s";
      }

      
      //
      //
      // Finish up
      //
      //

      if (tempConfig.rds_type=="se") {
        
      } else if (tempConfig.rds_type=="rac") {
        tempConfig.summaryText += " + " +
        0.5 * tempConfig.shape_optimize_vcpu * tempConfig.rac_nodes +
        " Oracle Processor Licenses";
         
      } else {
        tempConfig.summaryText += " + " +
        0.5 * tempConfig.shape_optimize_vcpu * mazFactor +
        " Oracle Processor Licenses";
      }

      tempConfig.ebs_backup_summaryText1 =
        "RDS Backup: First " +
        working_DiskSpace +
        " GB of backup storage is included (Free backup for up to 100% of provisioned storage).  Estimated size of snapshots is " +
        rdsSnapshotSize.toFixed(0) +
        " GB.";
      tempConfig.ebs_backup_summaryText2 =
        "RDS Backup Monthly estimate (Additional " +
        Math.max(0, 1 * rdsSnapshotSize - working_DiskSpace).toFixed(0) +
        " GB)=$" +
        Math.max(0, regionInfo.rds_backup_gb * (1 * rdsSnapshotSize - working_DiskSpace)).toFixed(
          2
        );

      tempConfig.monthly_estimate=Math.round(tempCost*100)/100;





        
        
        // now finish up matchConfig stuff        
        config.rds_shape = tempConfig.shape;
        config.targetconfig_rds = tempConfig;
        config.RDSbenefits = "Agility, Ease of Management, Strong HA+DR+Security+Performance, Lower Overall Costs";
        config.targetconfig_rds.closeness_string = rdsItemSelected.closeness_string;
        config.targetconfig_rds.match_string = rdsItemSelected.match_string;
        config.targetconfig_rds.gap_memory = rdsItemSelected.gap_memory;
        config.targetconfig_rds.gap_throughput = rdsItemSelected.gap_throughput ;
        config.targetconfig_rds.gap_iops = rdsItemSelected.gap_iops ;
        config.targetconfig_rds.gap_vcpu = rdsItemSelected.gap_vcpu ;

        
        
        
  
        //calculate surplus provisioning
        let multiplier = 1;
        if (tempConfig.rds_type=="rac") multiplier=1*tempConfig.rac_nodes;
        
        config.addlMaxVCPU = "";
        if ((tempConfig.shape_max_vcpu*multiplier - working_VCPU)>0) {
          config.addlMaxVCPU = "additional "+(tempConfig.shape_max_vcpu*multiplier - working_VCPU).toFixed(1)+" VCPUs";
        }
        config.addlOptimVCPU = "";
        if ((tempConfig.shape_optimize_vcpu*multiplier - working_VCPU)>0) {
          config.addlOptimVCPU = "additional "+(tempConfig.shape_optimize_vcpu*multiplier - working_VCPU).toFixed(1)+" VCPUs";
        }
        config.addlMemory = "";
        if ((tempConfig.shape_memory*multiplier - working_Memory)>0) {
          config.addlMemory = "additional "+(tempConfig.shape_memory*multiplier - working_Memory)+" GB";
        }
        // if (tempConfig.ebs_baseline_Throughput==undefined) {
        //   if (tempConfig.ebs_data_type=='gp2') {
        //     tempConfig.ebs_baseline_Throughput = Math.min(tempConfig.shape_throughput*multiplier,1000);
        //   }
        //   if (tempConfig.ebs_data_type=='gp3') {
        //     tempConfig.ebs_baseline_Throughput = Math.min(tempConfig.shape_throughput*multiplier,4000);
        //   }
        //   if (tempConfig.ebs_data_type=='io1') {
        //     tempConfig.ebs_baseline_Throughput = Math.min(tempConfig.shape_throughput*multiplier,4000);
        //   }

        // }  
        
        var combined_throughput = Math.min(tempConfig.shape_throughput*multiplier,tempConfig.ebs_burst_Throughput);
        var combined_basethroughput = Math.min( (tempConfig.ebs_baseline_Throughput>0) ? tempConfig.ebs_baseline_Throughput : tempConfig.ebs_burst_Throughput,
                                               ( (tempConfig.shape_baselinethroughput>0) ? tempConfig.shape_baselinethroughput : tempConfig.shape_throughput )*multiplier );
        // console.log("combined thru,base", combined_throughput, combined_basethroughput);
        // console.log("thru, origThru", working_Throughput, config.OrigThroughput);
        
        config.OrigthroughputtargetValues = "";
        config.OrigthroughputtargetLabels = "";
        config.ThroughputtargetValues = "";
        config.ThroughputtargetLabels = "";
        var ExadataDisplay=false;
        
        //is there Caching
        if (config.AdjustedThroughput) {
           config.OrigthroughputtargetLabels = config.OrigthroughputtargetLabels + "TargetWithCaching:";
           config.OrigthroughputtargetValues = config.OrigthroughputtargetValues + working_Throughput + ":";
           config.ThroughputtargetLabels = config.ThroughputtargetLabels + "TargetWithCaching:";
           config.ThroughputtargetValues = config.ThroughputtargetValues + working_Throughput + ":";
        }
        
        //is there no Exadata tuning?
         if ( (config.OrigThroughput*1 == config.Throughput*1) || ! ( (config.Exadata=='Yes')&&(config.frozen_prefs.smartscan_algo!='ss1') ) ) {
           config.OrigthroughputtargetLabels = config.OrigthroughputtargetLabels + "Target:";
           config.OrigthroughputtargetValues = config.OrigthroughputtargetValues + config.Throughput + ":";
           config.ThroughputtargetLabels = config.ThroughputtargetLabels + "Target:"; //these may not be used in this scenario
           config.ThroughputtargetValues = config.ThroughputtargetValues + config.Throughput + ":";
           
         } else {
           ExadataDisplay = true;
           config.OrigthroughputtargetLabels = config.OrigthroughputtargetLabels + "TargetWithTuning:ExadataPreTuning:";
           config.OrigthroughputtargetValues = config.OrigthroughputtargetValues + config.Throughput + ":" + config.OrigThroughput + ":" ;
           config.ThroughputtargetLabels = config.ThroughputtargetLabels + "TargetWithTuning:";
           config.ThroughputtargetValues = config.ThroughputtargetValues + config.Throughput + ":";
         }
        //is there bursting
        if ((combined_basethroughput )>0 && (combined_basethroughput!=combined_throughput)) {
           if (!ExadataDisplay) {
             config.OrigthroughputtargetLabels = config.OrigthroughputtargetLabels + "Baseline:Bursting";
             config.OrigthroughputtargetValues = config.OrigthroughputtargetValues + combined_basethroughput+ ":"+combined_throughput;
           }   
           config.ThroughputtargetLabels = config.ThroughputtargetLabels + "Baseline:Bursting";
           config.ThroughputtargetValues = config.ThroughputtargetValues + combined_basethroughput+ ":"+combined_throughput;
        } else {
           if (!ExadataDisplay) {
             config.OrigthroughputtargetLabels = config.OrigthroughputtargetLabels + "Available";
             config.OrigthroughputtargetValues = config.OrigthroughputtargetValues + combined_throughput;
           }   
           config.ThroughputtargetLabels = config.ThroughputtargetLabels + "Available";
           config.ThroughputtargetValues = config.ThroughputtargetValues + combined_throughput;
          
        }
        
        // console.log(config.OrigthroughputtargetLabels, config.OrigthroughputtargetValues, config.ThroughputtargetLabels, config.ThroughputtargetValues );
        
/*        
        // console.log("dave1110a",config.OrigThroughput , working_Throughput);
        if ((combined_basethroughput )>0 && (combined_basethroughput!=combined_throughput)) {
          if ( (config.OrigThroughput*1 == working_Throughput*1) || ! ( (config.Exadata=='Yes')&&(config.frozen_prefs.smartscan_algo!='ss1') ) ) {
            config.OrigthroughputtargetValues = ""+working_Throughput+":"+ combined_basethroughput+ ":"+combined_throughput ;
            config.OrigthroughputtargetLabels = "Target:Baseline:Bursting";
            if (config.AdjustedThroughput) {
              config.OrigthroughputtargetLabels = "Target:AdjustedTarget:Baseline:Bursting";
              config.OrigthroughputtargetValues = ""+ config.Throughput + ":" + working_Throughput+":"+ combined_basethroughput+ ":"+combined_throughput ;
            }  
          } else {  
            // this is the pre-adjustment graph (original Exadata)
            config.OrigthroughputtargetValues = ""+config.OrigThroughput+":"+  ":" ;
            config.OrigthroughputtargetLabels = "Exadata:Baseline:Bursting";
          }  
        } else {
          if ( (config.OrigThroughput*1 == working_Throughput*1) || ! ( (config.Exadata=='Yes')&&(config.frozen_prefs.smartscan_algo!='ss1') ) ) {
            config.OrigthroughputtargetValues = ""+working_Throughput+":"+ combined_throughput+ ":" ;
            config.OrigthroughputtargetLabels = "Target:Available:Bursting";
            if (config.AdjustedThroughput) {
              config.OrigthroughputtargetLabels = "Target:AdjustedTarget:Available";
              config.OrigthroughputtargetValues = ""+ config.Throughput + ":" + working_Throughput+ ":"+combined_throughput ;
            }
          } else {
            config.OrigthroughputtargetValues = ""+config.OrigThroughput+":"+ ":" ;
            config.OrigthroughputtargetLabels = "Exadata:Available:Bursting";
          }  
        }
        config.ThroughputtargetValues = "";
        config.ThroughputtargetLabels = "";
        if ((combined_basethroughput )>0 && (combined_basethroughput!=combined_throughput)) {
          config.ThroughputtargetValues = ""+working_Throughput+":"+ combined_basethroughput+ ":"+combined_throughput ;
          // Adjusted Target - ${(row.item.Throughput*1).toFixed(0)}:Baseline:Bursting
          config.ThroughputtargetLabels = "New Target - " + (working_Throughput*1).toFixed(0)+":Baseline:Bursting";
        } else {
          config.ThroughputtargetValues = ""+working_Throughput+":"+ combined_throughput+ ":" ;
          config.ThroughputtargetLabels = "New Target - " + (working_Throughput*1).toFixed(0)+":Available:Bursting";
        }
*/        
        

        var combined_IOPS = Math.min(tempConfig.shape_IOPS*multiplier,tempConfig.ebs_burst_IOPS);
        var combined_baseIOPS = Math.min( (tempConfig.ebs_baseline_IOPS>0) ? tempConfig.ebs_baseline_IOPS : tempConfig.ebs_burst_IOPS,
                                               ( (tempConfig.shape_baselineIOPS>0) ? tempConfig.shape_baselineIOPS : tempConfig.shape_IOPS )*multiplier );
        // console.log("combined IOPS,base", combined_IOPS, combined_baseIOPS);
        // console.log("IOPS, origIOPS", working_IOPS, config.OrigIOPS);
        if (tempConfig.ebs_burst_IOPS == tempConfig.ebs_baseline_IOPS) tempConfig.ebs_baseline_IOPS='';
        
        config.OrigIOPStargetValues = "";
        config.OrigIOPStargetLabels = "";
        config.IOPStargetValues = "";
        config.IOPStargetLabels = "";
        ExadataDisplay=false;
        
        //is there Caching
        if (config.AdjustedIOPS) {
           config.OrigIOPStargetLabels = config.OrigIOPStargetLabels + "TargetWithCaching:";
           config.OrigIOPStargetValues = config.OrigIOPStargetValues + working_IOPS + ":";
           config.IOPStargetLabels = config.IOPStargetLabels + "TargetWithCaching:";
           config.IOPStargetValues = config.IOPStargetValues + working_IOPS + ":";
        }
        
        //is there no Exadata tuning?
         if ( (config.OrigIOPS*1 == config.IOPS*1) || ! ( (config.Exadata=='Yes')&&(config.frozen_prefs.smartscan_algo!='ss1') ) ) {
           config.OrigIOPStargetLabels = config.OrigIOPStargetLabels + "Target:";
           config.OrigIOPStargetValues = config.OrigIOPStargetValues + config.IOPS + ":";
           config.IOPStargetLabels = config.IOPStargetLabels + "Target:"; //these may not be used in this scenario
           config.IOPStargetValues = config.IOPStargetValues + config.IOPS + ":";
           
         } else {
           ExadataDisplay = true;
           config.OrigIOPStargetLabels = config.OrigIOPStargetLabels + "TargetWithTuning:ExadataPreTuning:";
           config.OrigIOPStargetValues = config.OrigIOPStargetValues + config.IOPS + ":" + config.OrigIOPS + ":" ;
           config.IOPStargetLabels = config.IOPStargetLabels + "TargetWithTuning:";
           config.IOPStargetValues = config.IOPStargetValues + config.IOPS + ":";
         }
        //is there bursting
        if ((combined_baseIOPS )>0 && (combined_baseIOPS!=combined_IOPS)) {
           if (!ExadataDisplay) {
             config.OrigIOPStargetLabels = config.OrigIOPStargetLabels + "Baseline:Bursting";
             config.OrigIOPStargetValues = config.OrigIOPStargetValues + combined_baseIOPS+ ":"+combined_IOPS;
           }   
           config.IOPStargetLabels = config.IOPStargetLabels + "Baseline:Bursting";
           config.IOPStargetValues = config.IOPStargetValues + combined_baseIOPS+ ":"+combined_IOPS;
        } else {
           if (!ExadataDisplay) {
             config.OrigIOPStargetLabels = config.OrigIOPStargetLabels + "Available";
             config.OrigIOPStargetValues = config.OrigIOPStargetValues + combined_IOPS;
           }   
           config.IOPStargetLabels = config.IOPStargetLabels + "Available";
           config.IOPStargetValues = config.IOPStargetValues + combined_IOPS;
          
        }
        
        // console.log(config.OrigIOPStargetLabels, config.OrigIOPStargetValues, config.IOPStargetLabels, config.IOPStargetValues );        
        
 /*       
        config.OrigIOPStargetValues = "";
        config.OrigIOPStargetLabels = "";
        if ((combined_baseIOPS )>0 && (combined_baseIOPS!=combined_IOPS)) {
          if ( (config.OrigIOPS*1 == working_IOPS*1) || ! ( (config.Exadata=='Yes')&&(config.frozen_prefs.smartscan_algo!='ss1') ) ) {
            config.OrigIOPStargetValues = ""+working_IOPS+":"+ combined_baseIOPS+ ":"+combined_IOPS ;
            config.OrigIOPStargetLabels = "Target:Baseline:Bursting";
          } else {  
            config.OrigIOPStargetValues = ""+config.OrigIOPS+":"+  ":" ;
            config.OrigIOPStargetLabels = "Target:Baseline:Bursting";
          }  
        } else {
          if ( (config.OrigIOPS*1 == working_IOPS*1) || ! ( (config.Exadata=='Yes')&&(config.frozen_prefs.smartscan_algo!='ss1') ) ) {
            config.OrigIOPStargetValues = ""+working_IOPS+":"+ combined_IOPS+ ":" ;
            config.OrigIOPStargetLabels = "Target:Available:Bursting";
          } else {  
          config.OrigIOPStargetValues = ""+config.OrigIOPS+":"+  ":" ;
          config.OrigIOPStargetLabels = "Target:Available:Bursting";
          }
        }
        config.IOPStargetValues = "";
        config.IOPStargetLabels = "";
        if ((combined_baseIOPS )>0 && (combined_baseIOPS!=combined_IOPS)) {
          config.IOPStargetValues = ""+working_IOPS+":"+ combined_baseIOPS+ ":"+combined_IOPS  ;
          config.IOPStargetLabels = "New Target - " + (working_IOPS*1).toFixed(0)+":Baseline:Bursting";
        } else {
          config.IOPStargetValues = ""+working_IOPS+":"+ combined_IOPS+ ":" ;
          config.IOPStargetLabels = "New Target - " + (working_IOPS*1).toFixed(0)+":Available:Bursting";
        }
      // console.log("dave1110",tempConfig.ebs_burst_IOPS, config.IOPStargetValues);
*/

        config.addlThroughput = "";
        if ((combined_throughput - working_Throughput)>0) {
          config.addlThroughput = "additional "+(combined_throughput - working_Throughput)+" MB/s";
        } 
        config.addlBaselineThroughput = "";
        if ((combined_basethroughput - working_Throughput)>0) {
          config.addlBaselineThroughput = "additional "+(combined_basethroughput - working_Throughput)+" MB/s";
        }
        config.addlBaselineIOPS = "";
        if ((combined_baseIOPS - working_IOPS)>0) {
          config.addlBaselineIOPS = "additional "+(combined_baseIOPS - working_IOPS)+" IOPS";
        }
        config.addlBurstIOPS = "";
        if ((combined_IOPS - working_IOPS)>0) {
          config.addlBurstIOPS = "additional "+(combined_IOPS - working_IOPS)+" IOPS";
        }
        
      }

      //set  PostgreSQL suggestion
      config.extraInfo = "";
      if (1*config.COUNT_LINES_PLSQL*1<20000) {
        config.extraInfo = "Suggestion: This database does not have much PL/SQL.  Have you considered converting it to Aurora PostgreSQL?";
      }

       //set SE2 suggestion
       //strip out SE2-LI if any
      if (config.SE2_Comments==undefined) config.SE2_Comments="";
      if (config.SE2_Comments.indexOf("[SE2-LI")>=0) {
        config.SE2_Comments = config.SE2_Comments.slice(0,config.SE2_Comments.indexOf("[SE2-LI"));
      } 
      var SE2string="";
      // determine SE2-LI maximums from the available shapes in this region
      var se2_max_vcpu = 0;
      var se2_max_throughput = 0;
      var se2_max_iops = 0;
      var se2_max_memory = 0;
      var rdsItems_se2 = regionInfo.shapes || [];
      for (var si = 0; si < rdsItems_se2.length; si++) {
        if (rdsItems_se2[si].OracleLIse2_OD != null) {
          if (rdsItems_se2[si].VCPU*1 > se2_max_vcpu) se2_max_vcpu = rdsItems_se2[si].VCPU*1;
          if (rdsItems_se2[si].Throughput*1 > se2_max_throughput) se2_max_throughput = rdsItems_se2[si].Throughput*1;
          if (rdsItems_se2[si].IOPS*1 > se2_max_iops) se2_max_iops = rdsItems_se2[si].IOPS*1;
          if (rdsItems_se2[si].Memory*1 > se2_max_memory) se2_max_memory = rdsItems_se2[si].Memory*1;
        }
      }
      if (working_VCPU*1>se2_max_vcpu && se2_max_vcpu>0) { SE2string+= " VCPU>"+se2_max_vcpu; }
      if (working_Throughput*1>se2_max_throughput && se2_max_throughput>0) { SE2string+= " Throughput>"+se2_max_throughput; }
      if (working_IOPS*1>se2_max_iops && se2_max_iops>0) { SE2string+= " IOPS>"+se2_max_iops; }
      if (working_Memory*1>se2_max_memory && se2_max_memory>0) { SE2string+= " Memory>"+se2_max_memory; }      
      config.considerSE2 = "";
      if ((config.SE2=='Yes') && (SE2string=="")) {
        config.considerSE2="Yes";
      }  else {
        if (SE2string!="") {
          config.SE2_Comments+=" [SE2-LI ruled out because "+SE2string+"]";
        }  
      }
      config.extraInfo2 = config.SE2_Comments;



      return config;
    }












export {pick_rds_shape};
export {pick_rac_shape};
export {calculate_rds_config};

